import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Check,
  ChevronRight,
  ClipboardList,
  HelpCircle,
  Minus,
  Pencil,
  Plus,
  Settings,
  SlidersHorizontal,
  Star,
  Trash2,
  Zap,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import { useIsAdmin } from "@/hooks/useAuth";
import type { RosterConfigObject } from "@/hooks/useRosterConfig";
import {
  type RosterConfigPreset,
  type RosterConfigVersionsResponse,
  useRosterConfigVersionsQuery,
} from "@/hooks/roster-craete/useRosterConfigVersionsQuery";
import { cn } from "@/lib/utils";
import useSyncRosterCreateConfigMutation from "@/pages/roster-create/hook/useSyncRosterCreateConfigMutation";
import { type RosterCreateConfigPayload } from "@/pages/roster-create/service/rosterCreateConfigPayload";
import { deleteRosterConfigPresetApi } from "@/pages/roster-create/services/rosterCreateApi";

type ConfigDraft = {
  offDays: number;
  maxConseqWork: number;
  maxConseqOff: number;
  maxNightPerMonth: number;
  threeSeqNight: boolean;
  weeklyOffGroup: boolean;
  offPlacementMode: boolean;
  offSwapEnabled: boolean;
  healthLeaveEnabled: boolean;
  healthLeaveWeekend: boolean;
  sleepOffEnabled: boolean;
  sleepOffCycle: number;
  banNightBeforeFixedWantedOff: boolean;
  fixedWantedUse: boolean;
  precepteeOn: boolean;
  precepteeShiftCount: boolean;
  offFirst: boolean;
  evenNights: boolean;
  notOneNight: boolean;
};

type PresetMeta = {
  configId: number | null;
  configVersion: number | null;
  configName: string;
  configMemo: string;
};

type SavePresetForm = {
  configName: string;
  configMemo: string;
};

type PendingReturnAction = "none" | "start";
type SaveTarget = "new" | "current" | "preview";
type ConfigStartMode = "choose" | "new" | "saved";
type SetupMode = "staged" | "full";
type ConfigStartSourceKind = "new" | "latest" | "preset";
type GenerateConfigDisposition = "auto" | "update-existing" | "create-new";
type StagedSetupStep = (typeof STAGED_SETUP_STEPS)[number];
type SavedConfigListTab = "all" | "favorites";
type SavedPickerSurface = "start" | "edit";

type RosterCreateConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (payload: RosterCreateConfigPayload) => void;
  isPending: boolean;
  pendingMsg?: string;
  disabled?: boolean;
  serverConfig?: RosterConfigObject | null;
  selectedGroupId?: string | null;
  workerCount?: number;
};

const DEFAULT_DRAFT: ConfigDraft = {
  offDays: 10,
  maxConseqWork: 5,
  maxConseqOff: 3,
  maxNightPerMonth: 15,
  threeSeqNight: true,
  weeklyOffGroup: false,
  offPlacementMode: false,
  offSwapEnabled: false,
  healthLeaveEnabled: false,
  healthLeaveWeekend: false,
  sleepOffEnabled: false,
  sleepOffCycle: 15,
  banNightBeforeFixedWantedOff: false,
  fixedWantedUse: true,
  precepteeOn: true,
  precepteeShiftCount: false,
  offFirst: true,
  evenNights: true,
  notOneNight: true,
};

const SHOW_SAVED_CONFIGS = true;
const MIN_STAGED_OFF_DAYS = 8;
const MIN_CONSEQ_OFF_DAYS = 3;
const MAX_CONSEQ_OFF_DAYS = 6;
const STAGED_SETUP_STEPS = [
  "기준 선택",
  "인원 반영",
  "OFF 설정",
  "근무 설정",
  "요청 반영",
] as const;
const STAGED_SETUP_STEP_DESCRIPTIONS: Record<
  (typeof STAGED_SETUP_STEPS)[number],
  string
> = {
  "기준 선택": "생성에서 가장 먼저 적용할 배정 방향을 고릅니다.",
  "인원 반영": "병동과 근무자 수, 프리셉티 인원 산정 기준을 확인합니다.",
  "OFF 설정": "월 OFF 수와 연속 OFF, OFF 초과 처리 기준을 확인합니다.",
  "근무 설정": "연속 근무와 월 나이트 상한 기준을 확인합니다.",
  "요청 반영": "원티드와 나이트 배정 요청을 생성에 반영할지 정합니다.",
};

const savedConfigFavoriteStorageKey = (groupId: string | null) =>
  `roster-config-favorites:${groupId || "default"}`;

const clampConseqOffDays = (value: number) =>
  Math.min(MAX_CONSEQ_OFF_DAYS, Math.max(MIN_CONSEQ_OFF_DAYS, value));

const toDraft = (config?: RosterConfigObject | null): ConfigDraft => ({
  offDays: Math.max(0, config?.off_days ?? DEFAULT_DRAFT.offDays),
  maxConseqWork: Math.max(
    0,
    config?.max_conseq_work ?? DEFAULT_DRAFT.maxConseqWork
  ),
  maxConseqOff: clampConseqOffDays(
    config?.max_conseq_off ?? DEFAULT_DRAFT.maxConseqOff
  ),
  maxNightPerMonth: Math.max(
    0,
    config?.max_nig_per_month ?? DEFAULT_DRAFT.maxNightPerMonth
  ),
  threeSeqNight: config?.three_seq_nig ?? DEFAULT_DRAFT.threeSeqNight,
  weeklyOffGroup: config?.weekly_off_group ?? DEFAULT_DRAFT.weeklyOffGroup,
  offPlacementMode: Boolean(
    config?.off_placement_mode ?? DEFAULT_DRAFT.offPlacementMode
  ),
  offSwapEnabled: config?.off_swap_enabled ?? DEFAULT_DRAFT.offSwapEnabled,
  healthLeaveEnabled:
    config?.health_leave_enabled ?? DEFAULT_DRAFT.healthLeaveEnabled,
  healthLeaveWeekend:
    config?.health_leave_weekend ?? DEFAULT_DRAFT.healthLeaveWeekend,
  sleepOffEnabled: config?.sleep_off_enabled ?? DEFAULT_DRAFT.sleepOffEnabled,
  sleepOffCycle: config?.sleep_off_cycle ?? DEFAULT_DRAFT.sleepOffCycle,
  banNightBeforeFixedWantedOff:
    config?.ban_night_before_fixed_wanted_off ??
    DEFAULT_DRAFT.banNightBeforeFixedWantedOff,
  fixedWantedUse: config?.fixed_wanted_use_yn ?? DEFAULT_DRAFT.fixedWantedUse,
  precepteeOn: config?.preceptee_on ?? DEFAULT_DRAFT.precepteeOn,
  precepteeShiftCount:
    config?.preceptee_shift_count ?? DEFAULT_DRAFT.precepteeShiftCount,
  offFirst: config?.off_first ?? DEFAULT_DRAFT.offFirst,
  evenNights: config?.even_nights ?? DEFAULT_DRAFT.evenNights,
  notOneNight: config?.not_one_night ?? DEFAULT_DRAFT.notOneNight,
});

function configDraftSignature(config?: RosterConfigObject | null): string {
  if (!config) return "";
  return [
    config.config_id ?? "",
    config.version ?? "",
    config.off_days ?? "",
    config.max_conseq_work ?? "",
    config.max_conseq_off ?? "",
    config.max_nig_per_month ?? "",
    config.three_seq_nig ? "1" : "0",
    config.weekly_off_group ? "1" : "0",
    config.off_placement_mode ? "1" : "0",
    config.off_swap_enabled ? "1" : "0",
    config.fixed_wanted_use_yn ? "1" : "0",
    config.preceptee_on ? "1" : "0",
    config.preceptee_shift_count ? "1" : "0",
    config.off_first ? "1" : "0",
    config.even_nights ? "1" : "0",
    config.not_one_night ? "1" : "0",
  ].join("|");
}

function mergeConfigPayloadForCache(
  baseConfig: RosterConfigObject | null,
  payload: RosterCreateConfigPayload
): RosterConfigObject | null {
  if (!baseConfig) return null;
  return {
    ...baseConfig,
    ...payload,
    config_id: payload.config_id ?? baseConfig.config_id,
  };
}

function configConditionSignature(payload: RosterCreateConfigPayload | null) {
  if (!payload) return "";
  const { config_name, config_memo, ...conditionPayload } = payload;
  void config_name;
  void config_memo;
  return JSON.stringify(conditionPayload);
}

function ConfigToggle({
  checked,
  disabled,
  label,
  note,
  onChange,
  variant = "default",
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  note?: string;
  onChange: (checked: boolean) => void;
  variant?: "default" | "compact";
}) {
  const isCompact = variant === "compact";

  return (
    <button
      type="button"
      aria-pressed={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "group flex w-full items-start gap-3 rounded-xl border text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-50",
        isCompact ? "px-3 py-2.5" : "px-3.5 py-2.5",
        checked
          ? "border-[#0E59C6]/35 bg-[#F8FBFF]"
          : "border-[#E4E7EC] bg-white hover:border-[#0E59C6]/35 hover:bg-[#F8FAFC]"
      )}
    >
      <span
        className={cn(
          "mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors",
          checked
            ? "border-[#084AB5] bg-white text-[#084AB5]"
            : "border-[#D0D5DD] bg-white text-transparent group-hover:border-[#0E59C6]/50"
        )}
        aria-hidden="true"
      >
        <Check className="size-4 stroke-[3.8]" />
      </span>
      <span className="min-w-0">
        <span
          className={cn(
            "block font-bold text-[#344054]",
            isCompact ? "text-sm" : "text-base"
          )}
        >
          {label}
        </span>
        {note && (
          <span
            className={cn(
              "mt-1 block leading-relaxed text-[#98A2B3]",
              isCompact ? "text-xs" : "text-sm"
            )}
          >
            {note}
          </span>
        )}
      </span>
    </button>
  );
}

function NumberValue({
  label,
  max,
  min = 0,
  note,
  noteTone = "default",
  onChange,
  suffix,
  value,
  variant = "default",
}: {
  label: string;
  max?: number;
  min?: number;
  note?: string;
  noteTone?: "default" | "caution";
  onChange: (value: number) => void;
  suffix: string;
  value: number;
  variant?: "default" | "staged";
}) {
  const isStaged = variant === "staged";

  const updateValue = (nextValue: number) => {
    const minValue = Math.max(0, min);
    const clampedMin = Math.max(minValue, nextValue);
    onChange(max === undefined ? clampedMin : Math.min(max, clampedMin));
  };

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 rounded-xl border border-[#E4E7EC] bg-white transition focus-within:border-[#0E59C6]/40 focus-within:shadow-[0_0_0_3px_rgba(14,89,198,0.08)]",
        isStaged ? "px-[18px] py-4" : "px-4 py-3.5"
      )}
    >
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block font-bold text-[#344054]",
            isStaged ? "text-[15px]" : "text-sm"
          )}
        >
          {label}
        </span>
        {note && (
          <span
            className={cn(
              "mt-1 block leading-relaxed",
              noteTone === "caution" ? "text-[#B54708]" : "text-[#98A2B3]",
              isStaged ? "text-sm" : "text-xs"
            )}
          >
            {note}
          </span>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        <span
          className={cn(
            "flex items-center gap-1.5 rounded-xl bg-white ring-1 ring-[#D0D5DD] transition-colors focus-within:ring-[#0E59C6]",
            isStaged ? "h-[52px] px-4" : "h-12 px-3"
          )}
        >
          <input
            type="number"
            min={Math.max(0, min)}
            max={max}
            aria-label={label}
            value={value}
            onChange={(event) => {
              const nextValue = Number(event.currentTarget.value);
              updateValue(Number.isFinite(nextValue) ? nextValue : 0);
            }}
            className={cn(
              "border-0 bg-transparent px-0 text-center font-bold text-[#101828] outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
              isStaged ? "h-11 w-14 text-xl" : "h-10 w-12 text-lg"
            )}
          />
          <span className="text-sm font-bold text-[#667085]">{suffix}</span>
        </span>
        <span className="flex flex-col">
          <button
            type="button"
            aria-label="증가"
            title={`${label} 증가`}
            onClick={() => updateValue(value + 1)}
            className="flex size-6 items-center justify-center rounded-md bg-[#f8f8f8] text-[#667085] transition hover:bg-[#EFF4FF] hover:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:text-[#D0D5DD]"
            disabled={max !== undefined && value >= max}
          >
            <Plus className="size-3.5 stroke-[3]" />
          </button>
          <button
            type="button"
            aria-label="감소"
            title={`${label} 감소`}
            onClick={() => updateValue(value - 1)}
            className="flex size-6 items-center justify-center rounded-md bg-[#f8f8f8] text-[#667085] transition hover:bg-[#EFF4FF] hover:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:text-[#D0D5DD]"
            disabled={value <= Math.max(0, min)}
          >
            <Minus className="size-3.5 stroke-[3]" />
          </button>
        </span>
      </span>
    </div>
  );
}

function ConfigCard({
  action,
  badge,
  children,
  className,
  description,
  title,
}: {
  action?: React.ReactNode;
  badge?: string;
  children: React.ReactNode;
  className?: string;
  description: string;
  title: string;
}) {
  return (
    <section
      className={cn(
        "min-h-[188px] rounded-xl border border-[#E4E7EC] bg-white shadow-[0_8px_22px_rgba(16,24,40,0.04)]",
        className
      )}
    >
      <div className="flex h-[46px] items-center justify-between rounded-t-xl bg-[#F5F8FC] px-5">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="truncate text-base font-bold text-[#101828]">
            {title}
          </h3>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-[#98A2B3] transition-colors hover:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30"
                  aria-label={`${title} 설명`}
                >
                  <HelpCircle className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="start"
                className="max-w-[280px] text-xs leading-5"
              >
                {description}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {badge && (
          <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#667085] ring-1 ring-[#E4E7EC]">
            {badge}
          </span>
        )}
      </div>
      <div className="px-5 py-3.5">
        {action && (
          <div className="mb-2 flex items-center justify-end">{action}</div>
        )}
        <div className="space-y-1">{children}</div>
      </div>
    </section>
  );
}

function SkeletonBlock({ className }: { className?: string }) {
  return (
    <div className={cn("animate-pulse rounded-md bg-[#E9EEF5]", className)} />
  );
}

function ConfigModalSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        {[0, 1].map((index) => (
          <section
            key={index}
            className="h-[190px] rounded-[22px] border border-[#E4E7EC] bg-white p-7 shadow-[0_10px_24px_rgba(16,24,40,0.05)]"
          >
            <div className="flex items-start justify-between">
              <SkeletonBlock className="size-16 rounded-2xl" />
              {index === 0 ? (
                <SkeletonBlock className="h-7 w-14 rounded-full" />
              ) : null}
            </div>
            <div className="mt-9 space-y-3">
              <SkeletonBlock className="h-6 w-36" />
              <SkeletonBlock className="h-4 w-[72%]" />
            </div>
          </section>
        ))}
      </div>

      <section className="flex min-h-[92px] items-center gap-5 rounded-[22px] border border-[#E4E7EC] bg-white px-7 shadow-[0_10px_24px_rgba(16,24,40,0.04)]">
        <SkeletonBlock className="size-14 shrink-0 rounded-2xl" />
        <div className="min-w-0 flex-1 space-y-2.5">
          <SkeletonBlock className="h-5 w-44" />
          <SkeletonBlock className="h-4 w-[46%]" />
        </div>
        <SkeletonBlock className="h-5 w-5 shrink-0 rounded-full" />
      </section>

      <div className="space-y-2 pt-3">
        <SkeletonBlock className="h-4 w-52" />
        <SkeletonBlock className="h-4 w-72 max-w-full" />
      </div>
    </div>
  );
}

function getHighlightedSegments(text: string, searchTerm: string) {
  const needle = searchTerm.trim();
  if (!needle) return [{ text, isMatch: false }];

  const segments: { text: string; isMatch: boolean }[] = [];
  const lowerText = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let cursor = 0;
  let matchIndex = lowerText.indexOf(lowerNeedle, cursor);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      segments.push({
        text: text.slice(cursor, matchIndex),
        isMatch: false,
      });
    }
    const matchEnd = matchIndex + needle.length;
    segments.push({
      text: text.slice(matchIndex, matchEnd),
      isMatch: true,
    });
    cursor = matchEnd;
    matchIndex = lowerText.indexOf(lowerNeedle, cursor);
  }

  if (cursor < text.length) {
    segments.push({
      text: text.slice(cursor),
      isMatch: false,
    });
  }

  return segments.length > 0 ? segments : [{ text, isMatch: false }];
}

function HighlightedPresetText({
  className,
  searchTerm,
  text,
}: {
  className?: string;
  searchTerm: string;
  text: string;
}) {
  return (
    <span className={className}>
      {getHighlightedSegments(text, searchTerm).map((segment, index) =>
        segment.isMatch ? (
          <mark
            key={`${segment.text}-${index}`}
            className="rounded bg-[#FFF3BF] px-0.5 text-inherit"
          >
            {segment.text}
          </mark>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        )
      )}
    </span>
  );
}

function SavedConfigPickerDialog({
  deletingPresetId,
  disabled,
  emptyDescription,
  isOpen,
  isPresetListLoading,
  isPresetLoading,
  onClose,
  onDeletePreset,
  onEditPresetMeta,
  onSelectPreset,
  presets,
  currentPresetId,
  favoriteStorageKey,
  selectedPresetId,
  renderSelectedPresetModeActions,
}: {
  deletingPresetId: number | null;
  disabled?: boolean;
  emptyDescription: string;
  isOpen: boolean;
  isPresetListLoading: boolean;
  isPresetLoading: boolean;
  onClose: () => void;
  onDeletePreset: (preset: RosterConfigPreset) => void;
  onEditPresetMeta: (preset: RosterConfigPreset) => void;
  onSelectPreset: (preset: RosterConfigPreset) => void;
  presets: RosterConfigPreset[];
  currentPresetId: number | null;
  favoriteStorageKey: string;
  selectedPresetId: number | null;
  renderSelectedPresetModeActions?: (
    preset: RosterConfigPreset,
    closeActions: () => void
  ) => ReactNode;
}) {
  const [searchInput, setSearchInput] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [activeListTab, setActiveListTab] = useState<SavedConfigListTab>("all");
  const [actionPresetId, setActionPresetId] = useState<number | null>(null);
  const [favoritePresetIds, setFavoritePresetIds] = useState<readonly number[]>(
    []
  );
  const normalizedSearchTerm = activeSearchTerm.trim().toLowerCase();
  const isDeletingPreset = deletingPresetId !== null;
  const canClose = !isPresetLoading && !isDeletingPreset;
  const handleClose = () => {
    if (!canClose) return;
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const storedValue = window.localStorage.getItem(favoriteStorageKey);
    if (!storedValue) {
      setFavoritePresetIds([]);
      return;
    }

    try {
      const parsedValue: unknown = JSON.parse(storedValue);
      setFavoritePresetIds(
        Array.isArray(parsedValue)
          ? parsedValue.filter(
              (value): value is number =>
                typeof value === "number" && Number.isInteger(value)
            )
          : []
      );
    } catch {
      setFavoritePresetIds([]);
    }
  }, [favoriteStorageKey, isOpen]);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setActiveSearchTerm(searchInput);
    }, 250);

    return () => window.clearTimeout(debounceTimer);
  }, [searchInput]);

  useEffect(() => {
    if (!isOpen) {
      setActionPresetId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (actionPresetId === null) return;

    const handlePointerDownOutside = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-roster-config-action-root='true']")) return;
      setActionPresetId(null);
    };

    document.addEventListener("pointerdown", handlePointerDownOutside, true);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePointerDownOutside,
        true
      );
    };
  }, [actionPresetId]);

  const favoritePresetIdSet = useMemo(
    () => new Set(favoritePresetIds),
    [favoritePresetIds]
  );
  const favoritePresetCount = useMemo(
    () =>
      presets.filter((preset) => favoritePresetIdSet.has(preset.config_id))
        .length,
    [favoritePresetIdSet, presets]
  );

  const filteredPresets = useMemo(() => {
    const tabFilteredPresets =
      activeListTab === "favorites"
        ? presets.filter((preset) => favoritePresetIdSet.has(preset.config_id))
        : presets;
    if (!normalizedSearchTerm) return tabFilteredPresets;
    return tabFilteredPresets.filter((preset) => {
      const searchable = [
        preset.config_name ?? "",
        preset.config_memo ?? "",
        `설정 v${preset.version}`,
      ]
        .join(" ")
        .toLowerCase();
      return searchable.includes(normalizedSearchTerm);
    });
  }, [activeListTab, favoritePresetIdSet, normalizedSearchTerm, presets]);

  const toggleFavoritePreset = (configId: number) => {
    setFavoritePresetIds((prev) => {
      const nextIds = prev.includes(configId)
        ? prev.filter((id) => id !== configId)
        : [...prev, configId];
      window.localStorage.setItem(favoriteStorageKey, JSON.stringify(nextIds));
      return nextIds;
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        overlayClassName="bg-black/35"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleClose();
        }}
        className="w-[min(720px,calc(100vw-40px))] max-w-none gap-0 overflow-hidden rounded-xl border-0 bg-white p-0 font-pretendard shadow-xl"
      >
        <div className="border-b border-[#E4E7EC] px-6 py-5">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="이전으로 돌아가기"
              disabled={!canClose}
              onClick={handleClose}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-[#E4E7EC] bg-white text-[#667085] transition-colors hover:border-[#B8CAE8] hover:bg-[#F8FAFC] hover:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
            </button>
            <DialogTitle className="text-xl font-bold text-[#101828]">
              저장된 설정 불러오기
            </DialogTitle>
          </div>
          <DialogDescription className="mt-1 text-sm leading-6 text-[#667085]">
            저장된 설정을 선택한 뒤 단계별 설정 또는 전체 설정으로 엽니다.
          </DialogDescription>
        </div>
        <div className="p-5">
          <div className="min-w-0">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {[
                {
                  count: presets.length,
                  label: "전체",
                  value: "all" as const,
                },
                {
                  count: favoritePresetCount,
                  label: "즐겨찾기",
                  value: "favorites" as const,
                },
              ].map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  aria-pressed={activeListTab === tab.value}
                  onClick={() => setActiveListTab(tab.value)}
                  className={cn(
                    "inline-flex h-9 items-center justify-center gap-1.5 rounded-full border px-3.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30",
                    activeListTab === tab.value
                      ? "border-[#0E59C6] bg-[#F8FBFF] text-[#0E59C6]"
                      : "border-[#E4E7EC] bg-white text-[#667085] hover:border-[#B8CAE8] hover:text-[#344054]"
                  )}
                >
                  {tab.label}
                  <span className="text-xs font-bold opacity-80">
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            <SearchInput
              aria-label="저장된 설정 검색"
              name="roster-config-preset-search"
              autoComplete="off"
              placeholder="설정명 또는 메모 검색..."
              value={searchInput}
              onChange={(event) => setSearchInput(event.currentTarget.value)}
              showSearchButton={false}
              containerClassName="h-12 w-full rounded-xl border-[#D0D5DD]"
              inputClassName="h-12 pl-4 pr-11 text-base font-medium text-[#101828] placeholder:text-[#98A2B3]"
            />

            {isPresetListLoading ? (
              <div className="mt-4 rounded-xl border border-[#E4E7EC] bg-[#F8FAFC] p-3">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="rounded-xl border border-[#E4E7EC] bg-white p-4"
                  >
                    <SkeletonBlock className="h-5 w-40" />
                    <SkeletonBlock className="mt-3 h-3 w-full" />
                    <SkeletonBlock className="mt-2 h-3 w-[55%]" />
                  </div>
                ))}
              </div>
            ) : presets.length > 0 ? (
              <div className="mt-4 h-[calc(100dvh-340px)] overflow-y-auto rounded-xl border border-[#E4E7EC] bg-[#F8FAFC] p-3">
                {filteredPresets.length > 0 ? (
                  <div className="grid gap-3">
                    {filteredPresets.map((preset) => {
                      const isDeleting = deletingPresetId === preset.config_id;
                      const isSelected = preset.config_id === selectedPresetId;
                      const hasPresetActions =
                        renderSelectedPresetModeActions !== undefined;
                      const isActionPopoverOpen =
                        hasPresetActions && actionPresetId === preset.config_id;
                      const isVisuallySelected = hasPresetActions
                        ? isActionPopoverOpen
                        : isSelected;
                      const isCurrentPreset =
                        preset.config_id === currentPresetId;
                      const isFavorite = favoritePresetIdSet.has(
                        preset.config_id
                      );
                      const presetName =
                        preset.config_name || `설정 v${preset.version}`;

                      return (
                        <article
                          key={preset.config_id}
                          data-roster-config-action-root="true"
                          className={cn(
                            "relative rounded-xl border bg-white p-4 transition-colors focus-within:border-[#0E59C6]/70",
                            hasPresetActions &&
                              !disabled &&
                              !isPresetLoading &&
                              !isDeleting &&
                              "cursor-pointer",
                            isActionPopoverOpen && "z-20",
                            isVisuallySelected
                              ? "border-[#0E59C6] shadow-[0px_0px_15px_#0072ff70]"
                              : "border-[#E4E7EC] shadow-none hover:border-[#B8CAE8]"
                          )}
                        >
                          {hasPresetActions ? (
                            <button
                              type="button"
                              aria-label={`${presetName} 불러오기 방식 선택`}
                              disabled={
                                disabled || isPresetLoading || isDeleting
                              }
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onSelectPreset(preset);
                                setActionPresetId((prev) =>
                                  prev === preset.config_id
                                    ? null
                                    : preset.config_id
                                );
                              }}
                              className="absolute inset-0 z-[2] rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed"
                            />
                          ) : (
                            <button
                              type="button"
                              aria-label={`${presetName} 선택`}
                              aria-pressed={isSelected}
                              disabled={disabled || isPresetLoading}
                              onClick={() => {
                                setActionPresetId(null);
                                onSelectPreset(preset);
                              }}
                              className="absolute inset-0 z-[2] rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed"
                            />
                          )}
                          <div
                            aria-hidden={hasPresetActions ? undefined : true}
                            className="pointer-events-none relative z-[1] block w-full pl-10 text-left"
                          >
                            <div className="flex min-w-0 items-start justify-between gap-3 pr-16">
                              <div className="min-w-0">
                                <div className="flex min-w-0 items-center gap-2">
                                  <p className="min-w-0 truncate text-base font-bold text-[#101828]">
                                    <HighlightedPresetText
                                      searchTerm={activeSearchTerm}
                                      text={presetName}
                                    />
                                  </p>
                                  {isCurrentPreset && (
                                    <span className="shrink-0 rounded-full bg-[#EAF2FF] px-2.5 py-1 text-xs font-bold text-[#0E59C6]">
                                      현재 설정
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1.5 line-clamp-1 text-sm leading-6 text-[#667085]">
                                  <HighlightedPresetText
                                    searchTerm={activeSearchTerm}
                                    text={preset.config_memo || "메모 없음"}
                                  />
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-3 border-t border-[#F2F4F7] pt-3">
                              <div
                                className="flex items-center gap-x-4 text-sm"
                                aria-label="설정 요약"
                              >
                                <span>
                                  <span className="font-medium text-[#98A2B3]">
                                    OFF
                                  </span>{" "}
                                  <span className="font-semibold text-[#344054]">
                                    {preset.summary.off_days}
                                  </span>
                                </span>
                                <span>
                                  <span className="font-medium text-[#98A2B3]">
                                    D
                                  </span>{" "}
                                  <span className="font-semibold text-[#344054]">
                                    {preset.summary.day_req}
                                  </span>
                                </span>
                                <span>
                                  <span className="font-medium text-[#98A2B3]">
                                    E
                                  </span>{" "}
                                  <span className="font-semibold text-[#344054]">
                                    {preset.summary.eve_req}
                                  </span>
                                </span>
                                <span>
                                  <span className="font-medium text-[#98A2B3]">
                                    N
                                  </span>{" "}
                                  <span className="font-semibold text-[#344054]">
                                    {preset.summary.nig_req}
                                  </span>
                                </span>
                              </div>
                              <span className="shrink-0 text-xs font-semibold text-[#98A2B3]">
                                {formatSavedAt(preset.last_saved_at)}
                              </span>
                            </div>
                          </div>
                          {isActionPopoverOpen &&
                            renderSelectedPresetModeActions && (
                              <div
                                role="menu"
                                onPointerDown={(event) => {
                                  event.stopPropagation();
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                                className="absolute right-4 top-[calc(100%-8px)] z-[40] w-[252px] origin-top-right animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 rounded-xl border border-[#DCE6F7] bg-white p-1.5 shadow-[0_14px_28px_rgba(16,24,40,0.16)] duration-150 ease-out"
                              >
                                {renderSelectedPresetModeActions(preset, () =>
                                  setActionPresetId(null)
                                )}
                              </div>
                            )}
                          <button
                            type="button"
                            aria-pressed={isFavorite}
                            aria-label={`${presetName} 즐겨찾기 ${
                              isFavorite ? "해제" : "추가"
                            }`}
                            disabled={disabled || isPresetLoading || isDeleting}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              toggleFavoritePreset(preset.config_id);
                            }}
                            className={cn(
                              "absolute left-4 top-4 z-[3] inline-flex size-8 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-40",
                              isFavorite
                                ? "bg-transparent text-[#F59E0B]"
                                : "text-[#98A2B3] hover:bg-[#F8FAFC] hover:text-[#667085]"
                            )}
                          >
                            <Star
                              className={cn(
                                "size-4",
                                isFavorite &&
                                  "fill-[#F59E0B] stroke-[#F59E0B] [&_path]:fill-[#F59E0B]"
                              )}
                              aria-hidden="true"
                            />
                          </button>
                          <div className="absolute right-4 top-4 z-[3] flex items-center gap-1">
                            <button
                              type="button"
                              aria-label={`${presetName} 설명 정보 수정`}
                              disabled={
                                disabled || isPresetLoading || isDeleting
                              }
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setActionPresetId(null);
                                onEditPresetMeta(preset);
                              }}
                              className="inline-flex size-8 items-center justify-center rounded-full text-[#98A2B3] transition-colors hover:bg-[#F8FAFC] hover:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <Pencil className="size-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`${presetName} 삭제`}
                              disabled={
                                disabled || isPresetLoading || isDeleting
                              }
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onDeletePreset(preset);
                              }}
                              className="inline-flex size-8 items-center justify-center rounded-full text-[#98A2B3] transition-colors hover:bg-[#FFF1F0] hover:text-[#E5484D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FDA29B] disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              {isDeleting ? (
                                <span className="size-4 animate-spin rounded-full border-2 border-[#FDA29B] border-t-transparent" />
                              ) : (
                                <Trash2 className="size-4" />
                              )}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <p className="text-base font-bold text-[#344054]">
                      {activeListTab === "favorites"
                        ? "즐겨찾기한 설정이 없습니다."
                        : "검색 결과가 없습니다."}
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#98A2B3]">
                      {activeListTab === "favorites"
                        ? "별표를 눌러 자주 쓰는 설정을 모아보세요."
                        : "설정명이나 메모를 다시 확인하세요."}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4 flex h-[520px] max-h-[calc(100dvh-340px)] flex-col items-center justify-center rounded-xl border border-dashed border-[#D0D5DD] bg-white text-center">
                <p className="text-base font-bold text-[#344054]">
                  저장된 설정이 없습니다.
                </p>
                <p className="mt-1 text-sm leading-6 text-[#98A2B3]">
                  {emptyDescription}
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfigStartChooser({
  deletingPresetId,
  disabled,
  onHidePresets,
  isPresetListLoading,
  isPresetLoading,
  isPresetPickerStackOpen,
  favoriteStorageKey,
  onDeletePreset,
  onEditPresetMeta,
  onSelectPreset,
  onShowPresets,
  onStartGenerate,
  onStartLatestSetup,
  onStartSetup,
  presets,
  selectedPreset,
  selectedSource,
  sourceConfig,
}: {
  deletingPresetId: number | null;
  disabled?: boolean;
  isPresetPickerStackOpen: boolean;
  onHidePresets: () => void;
  isPresetListLoading: boolean;
  isPresetLoading: boolean;
  favoriteStorageKey: string;
  onDeletePreset: (preset: RosterConfigPreset) => void;
  onEditPresetMeta: (preset: RosterConfigPreset) => void;
  onSelectPreset: (preset: RosterConfigPreset) => void;
  onShowPresets: () => void;
  onStartGenerate: (preset?: RosterConfigPreset) => void;
  onStartLatestSetup: (mode: SetupMode) => void;
  onStartSetup: (mode: SetupMode, preset?: RosterConfigPreset) => void;
  presets: RosterConfigPreset[];
  selectedPreset: RosterConfigPreset | null;
  selectedSource: ConfigStartSourceKind;
  sourceConfig: RosterConfigObject | null;
}) {
  const showsPresetList =
    selectedSource === "preset" || isPresetPickerStackOpen;
  const hasCurrentSavedConfig =
    sourceConfig?.config_id !== null &&
    sourceConfig?.config_id !== undefined &&
    sourceConfig?.version !== null &&
    sourceConfig?.version !== undefined;
  const sourceConfigLabel = hasCurrentSavedConfig
    ? sourceConfig?.config_name || `설정 v${sourceConfig.version}`
    : "현재 저장된 설정이 없습니다";
  const sourceConfigMemo = sourceConfig?.config_memo?.trim();
  return (
    <>
      <div className="flex flex-col gap-5">
        <div className="grid w-full grid-cols-2 gap-4">
          {[
            {
              badge: "권장",
              className:
                "border-[#3F73F6] bg-[#3F73F6] text-white shadow-[0_14px_28px_rgba(63,115,246,0.22)] hover:bg-[#3568EA]",
              description: "필수 항목부터 순서대로 확인합니다.",
              icon: ClipboardList,
              key: "staged",
              label: "단계별 설정",
              onClick: () => onStartLatestSetup("staged"),
              selected: false,
            },
            {
              badge: null,
              className:
                "border-[#7CA8CD] bg-[#7CA8CD] text-white shadow-[0_14px_28px_rgba(92,132,166,0.20)] hover:bg-[#709CC3]",
              description: "모든 조건을 한 화면에서 빠르게 조정합니다.",
              icon: SlidersHorizontal,
              key: "full",
              label: "전체 설정",
              onClick: () => onStartLatestSetup("full"),
              selected: false,
            },
          ].map((item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.key}
                type="button"
                disabled={disabled}
                onClick={item.onClick}
                className={cn(
                  "relative min-h-[172px] rounded-[20px] border p-5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-50",
                  item.className
                )}
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-xl bg-white/18 text-white ring-1 ring-white/20">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  {item.badge && (
                    <span className="rounded-full bg-white/18 px-2.5 py-1 text-xs font-bold text-white ring-1 ring-white/25">
                      {item.badge}
                    </span>
                  )}
                </span>
                <span className="mt-8 flex items-center gap-2 text-xl font-bold leading-tight">
                  {item.label}
                  {item.selected && (
                    <CheckCircle2 className="size-4 text-white" />
                  )}
                </span>
                <span className="mt-1.5 block text-sm font-semibold leading-6 text-white/85">
                  {item.description}
                </span>
                <ChevronRight
                  className="absolute bottom-5 right-5 size-5 text-white/85"
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
        <div className="rounded-[18px] border border-[#E4E7EC] bg-white p-4 shadow-[0_10px_24px_rgba(16,24,40,0.05)]">
          <div className="mb-2 flex items-center gap-1.5 px-1">
            <span className="text-xs font-bold text-[#667085]">최근 설정</span>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex size-4 items-center justify-center rounded-full text-[#98A2B3] transition-colors hover:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30"
                    aria-label="최신 기준 설명"
                  >
                    <HelpCircle className="size-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    최신 기준은 마지막으로 저장된 설정 버전을 기준으로 계속
                    불러옵니다.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <button
            type="button"
            aria-label="저장된 설정 불러오기"
            disabled={disabled}
            onClick={onShowPresets}
            className="flex w-full items-center gap-4 rounded-xl px-1 py-1 text-left transition-colors hover:bg-[#F8FAFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#F8FAFC] text-[#667085]">
              <Settings className="size-5" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 flex-wrap items-center gap-2 text-base font-bold text-[#101828]">
                {sourceConfigLabel}
                {hasCurrentSavedConfig && (
                  <span className="rounded-full bg-[#EAF2FF] px-2.5 py-1 text-xs font-bold text-[#0E59C6]">
                    현재 설정
                  </span>
                )}
              </span>
              <span className="mt-1 flex min-w-0 flex-wrap items-center gap-2 text-sm">
                <span className="min-w-0 truncate font-medium text-[#667085]">
                  {(hasCurrentSavedConfig && sourceConfigMemo) ||
                    (hasCurrentSavedConfig
                      ? "저장된 설정을 불러온 뒤 방식을 선택합니다."
                      : "저장하면 이곳에서 다시 불러올 수 있습니다.")}
                </span>
              </span>
            </span>
            <ChevronRight
              className="size-5 text-[#98A2B3]"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <SavedConfigPickerDialog
        deletingPresetId={deletingPresetId}
        disabled={disabled}
        emptyDescription="새 설정 또는 최근 설정으로 시작하세요."
        isOpen={showsPresetList}
        isPresetListLoading={isPresetListLoading}
        isPresetLoading={isPresetLoading}
        onClose={onHidePresets}
        onDeletePreset={onDeletePreset}
        onEditPresetMeta={onEditPresetMeta}
        onSelectPreset={onSelectPreset}
        presets={presets}
        currentPresetId={sourceConfig?.config_id ?? null}
        favoriteStorageKey={favoriteStorageKey}
        selectedPresetId={selectedPreset?.config_id ?? null}
        renderSelectedPresetModeActions={(preset, closeActions) => (
          <div className="grid gap-1">
            <button
              type="button"
              disabled={disabled || isPresetLoading}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                closeActions();
                onStartSetup("staged", preset);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (event.detail === 0) {
                  closeActions();
                  onStartSetup("staged", preset);
                }
              }}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left text-[#101828] transition-colors hover:bg-[#F0F6FF] hover:text-[#0E59C6] focus-visible:bg-[#F0F6FF] focus-visible:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ClipboardList
                className="mt-0.5 size-4 shrink-0 text-[#0E59C6]"
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold">
                  단계별 설정으로 열기
                </span>
                <span className="mt-0.5 block text-xs font-medium text-[#667085]">
                  필수 항목부터 순서대로 설정
                </span>
              </span>
            </button>
            <button
              type="button"
              disabled={disabled || isPresetLoading}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                closeActions();
                onStartSetup("full", preset);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (event.detail === 0) {
                  closeActions();
                  onStartSetup("full", preset);
                }
              }}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left text-[#344054] transition-colors hover:bg-[#F0F6FF] hover:text-[#0E59C6] focus-visible:bg-[#F0F6FF] focus-visible:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <SlidersHorizontal
                className="mt-0.5 size-4 shrink-0 text-[#667085]"
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold">
                  전체 설정으로 열기
                </span>
                <span className="mt-0.5 block text-xs font-medium text-[#667085]">
                  모든 조건을 한 화면에서 설정
                </span>
              </span>
            </button>
            <div className="my-1 h-px bg-[#EEF2F7]" />
            <button
              type="button"
              disabled={disabled || isPresetLoading}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                closeActions();
                onStartGenerate(preset);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (event.detail === 0) {
                  closeActions();
                  onStartGenerate(preset);
                }
              }}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left text-[#101828] transition-colors hover:bg-[#F0F6FF] hover:text-[#0E59C6] focus-visible:bg-[#F0F6FF] focus-visible:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Zap
                className="mt-0.5 size-4 shrink-0 text-[#F59E0B]"
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold">
                  근무표 즉시 생성
                </span>
                <span className="mt-0.5 block text-xs font-medium text-[#667085]">
                  저장된 설정으로 즉시 생성
                </span>
              </span>
            </button>
          </div>
        )}
      />
    </>
  );
}
function SetupModeIconToggle({
  disabled,
  mode,
  onChange,
}: {
  disabled?: boolean;
  mode: SetupMode;
  onChange: (mode: SetupMode) => void;
}) {
  const options: readonly {
    icon: typeof ClipboardList;
    label: string;
    value: SetupMode;
  }[] = [
    { icon: ClipboardList, label: "단계별 설정", value: "staged" },
    { icon: SlidersHorizontal, label: "전체 설정", value: "full" },
  ];

  return (
    <div className="grid h-10 w-[92px] grid-cols-2 rounded-xl bg-[#F2F4F7] p-1">
      {options.map((option) => {
        const Icon = option.icon;

        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            aria-label={option.label}
            aria-pressed={mode === option.value}
            title={option.label}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-60",
              mode === option.value
                ? "bg-white text-[#0E59C6] shadow-sm"
                : "text-[#667085] hover:text-[#344054]"
            )}
          >
            <Icon className="size-4.5" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}

function SetupModeSwitch({
  contextLabel,
  description = "저장된 설정을 불러온 뒤 방식을 선택합니다.",
  disabled,
  onOpenPresets,
  notice,
  readOnly = false,
}: {
  contextLabel: string;
  description?: string;
  disabled?: boolean;
  notice?: React.ReactNode;
  onOpenPresets?: () => void;
  readOnly?: boolean;
}) {
  const content = (
    <>
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#F8FAFC] text-[#667085]">
        <Settings className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate text-sm font-bold text-[#101828]">
            {contextLabel}
          </span>
          <span className="shrink-0 rounded-full bg-[#EAF2FF] px-2 py-0.5 text-[11px] font-bold text-[#0E59C6]">
            현재 설정
          </span>
          {notice}
        </span>
        <span className="mt-1 block truncate text-xs font-semibold text-[#667085]">
          {description}
        </span>
      </span>
      {!readOnly && <ChevronRight className="size-4 shrink-0 text-[#98A2B3]" />}
    </>
  );

  return (
    <div className="mb-2">
      <div>
        {readOnly ? (
          <div className="flex w-full min-w-0 items-center gap-2.5 rounded-xl border border-[#E4E7EC] bg-white px-3 py-2 text-left shadow-[0_2px_8px_rgba(16,24,40,0.025)]">
            {content}
          </div>
        ) : (
          <button
            type="button"
            aria-label="저장된 설정 불러오기"
            disabled={disabled}
            onClick={onOpenPresets}
            className="flex w-full min-w-0 items-center gap-2.5 rounded-xl border border-[#E4E7EC] bg-white px-3 py-2 text-left shadow-[0_2px_8px_rgba(16,24,40,0.025)] transition-colors hover:border-[#B8CAE8] hover:bg-[#F8FAFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {content}
          </button>
        )}
      </div>
    </div>
  );
}

function GenerationConfirmSummary({
  draft,
  groupName,
  workerCount,
}: {
  draft: ConfigDraft;
  groupName: string;
  workerCount: number;
}) {
  const sections = [
    {
      items: [["배정 기준", draft.offFirst ? "근무 우선" : "OFF 균형"]],
      title: "기준 선택",
    },
    {
      items: [
        ["병동명", groupName],
        ["근무자 수", `${workerCount}명`],
        ["프리셉티", draft.precepteeShiftCount ? "포함" : "제외"],
      ],
      title: "인원 반영",
    },
    {
      items: [
        ["월 OFF", `${draft.offDays}일`],
        ["연속 OFF 상한", `${draft.maxConseqOff}일`],
        ["OFF 초과 연차", draft.offSwapEnabled ? "ON" : "OFF"],
      ],
      title: "OFF 설정",
    },
    {
      items: [
        ["연속 근무", `${draft.maxConseqWork}일`],
        ["월 나이트", `${draft.maxNightPerMonth}회`],
        ["나이트 3연속", draft.threeSeqNight ? "허용" : "제한"],
      ],
      title: "근무 설정",
    },
    {
      items: [
        ["원티드", draft.fixedWantedUse ? "ON" : "OFF"],
        ["나이트 균형", draft.evenNights ? "ON" : "OFF"],
        ["단독 나이트", draft.notOneNight ? "제한" : "허용"],
      ],
      title: "요청 반영",
    },
  ];

  return (
    <div className="space-y-6">
      {sections.map((section) => (
        <section key={section.title} className="min-w-0">
          <h4 className="text-sm font-bold text-[#101828]">{section.title}</h4>
          <div className="mt-2 flex min-w-0 flex-wrap gap-2">
            {section.items.map(([label, value]) => (
              <div
                key={label}
                className="inline-flex max-w-full items-center gap-2 rounded-xl bg-[#F6F8FA] px-3 py-2 text-sm"
              >
                <span className="shrink-0 text-[#6F788D]">{label}</span>
                <span className="min-w-0 truncate font-semibold text-[#101828]">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function StagedSetupPanel({
  contextDescription,
  contextLabel,
  disabled,
  draft,
  groupName,
  notice,
  onOpenPresets,
  onStepChange,
  onUpdateDraft,
  step,
  workerCount,
}: {
  contextDescription?: string;
  contextLabel: string;
  disabled?: boolean;
  draft: ConfigDraft;
  groupName: string;
  notice?: React.ReactNode;
  onOpenPresets: () => void;
  onStepChange: (step: number) => void;
  onUpdateDraft: <K extends keyof ConfigDraft>(
    key: K,
    value: ConfigDraft[K]
  ) => void;
  step: number;
  workerCount: number;
}) {
  const currentStep = STAGED_SETUP_STEPS[step] ?? STAGED_SETUP_STEPS[0];

  const stepContentByName: Record<StagedSetupStep, React.ReactNode> = {
    "기준 선택": (
      <div className="space-y-4 ">
        <div className="grid gap-3 md:grid-cols-2">
          {[
            {
              description: (
                <>
                  일자별 필요 인원을 먼저 맞추고
                  <br />
                  남는 칸을 OFF로 회수합니다.
                </>
              ),
              label: "근무 우선",
              value: true,
            },
            {
              description: (
                <>
                  월 OFF 기준을 먼저 맞추고
                  <br />
                  남는 칸을 근무로 배정합니다.
                </>
              ),
              label: "OFF 균형",
              value: false,
            },
          ].map((option) => (
            <button
              key={option.label}
              type="button"
              aria-pressed={draft.offFirst === option.value}
              onClick={() => onUpdateDraft("offFirst", option.value)}
              className={cn(
                "group min-h-[132px] cursor-pointer rounded-xl border bg-white p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30",
                draft.offFirst === option.value
                  ? "border-[#0E59C6] shadow-[0px_0px_15px_#0072ff70]"
                  : "border-[#E4E7EC] hover:border-[#0E59C6]/40 hover:bg-[#F8FAFC] hover:shadow-[0_6px_16px_rgba(14,89,198,0.10)]"
              )}
            >
              <span
                className={cn(
                  "flex items-center gap-2 text-base font-bold transition-colors",
                  draft.offFirst === option.value
                    ? "text-[#0E59C6]"
                    : "text-[#101828] group-hover:text-[#0E59C6]"
                )}
              >
                {draft.offFirst === option.value ? (
                  <Check className="size-4 shrink-0" aria-hidden="true" />
                ) : null}
                {option.label}
              </span>
              <span className="mt-3 block text-sm  leading-6 text-[#667085]">
                {option.description}
              </span>
            </button>
          ))}
        </div>
      </div>
    ),
    "인원 반영": (
      <div className="space-y-5">
        <section>
          <h4 className="text-sm font-bold text-[#101828]">인원 정보</h4>
          <div className="mt-2 flex min-w-0 flex-wrap gap-2">
            <div className="inline-flex max-w-full items-center gap-2 rounded-xl bg-[#f6f8fa] px-3 py-2 text-sm">
              <span className="shrink-0 text-[#6f788d]">병동명</span>
              <span className="min-w-0 truncate font-semibold text-[#101828]">
                {groupName}
              </span>
            </div>
            <div className="inline-flex max-w-full items-center gap-2 rounded-xl bg-[#f6f8fa] px-3 py-2 text-sm">
              <span className="shrink-0 text-[#6f788d]">근무자 수</span>
              <span className="min-w-0 truncate font-semibold text-[#101828]">
                {workerCount}명
              </span>
            </div>
          </div>
        </section>
        <section>
          <h4 className="mb-2 text-sm font-bold text-[#101828]">산정 기준</h4>
          <button
            type="button"
            aria-pressed={draft.precepteeShiftCount}
            onClick={() =>
              onUpdateDraft("precepteeShiftCount", !draft.precepteeShiftCount)
            }
            className={cn(
              "group flex w-full items-start gap-3 rounded-xl border px-3.5 py-2.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30",
              draft.precepteeShiftCount
                ? "border-[#0E59C6]/35 bg-[#F8FBFF]"
                : "border-[#E4E7EC] bg-white hover:border-[#0E59C6]/35 hover:bg-[#F8FAFC]"
            )}
          >
            <span
              className={cn(
                "mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-md border transition-colors",
                draft.precepteeShiftCount
                  ? "border-[#084AB5] bg-white text-[#084AB5]"
                  : "border-[#D0D5DD] bg-white text-transparent group-hover:border-[#0E59C6]/50"
              )}
              aria-hidden="true"
            >
              <Check className="size-4 stroke-[3.8]" />
            </span>
            <span className="min-w-0">
              <span className="block text-base font-bold text-[#344054]">
                프리셉티 포함
              </span>
              <span className="mt-1 block text-sm leading-relaxed text-[#98A2B3]">
                프리셉티 근무를 생성 기준 인원 산정에 포함합니다.
              </span>
            </span>
          </button>
        </section>
      </div>
    ),
    "요청 반영": (
      <div className="space-y-5">
        <section>
          <h4 className="mb-2 text-sm font-bold text-[#101828]">요청 조건</h4>
          <div className="space-y-3">
            <ConfigToggle
              checked={draft.fixedWantedUse}
              label="원티드 반영"
              note="확정 요청을 생성 기준에 포함합니다."
              onChange={(checked) => onUpdateDraft("fixedWantedUse", checked)}
            />
            <ConfigToggle
              checked={draft.evenNights}
              label="나이트 균형"
              note="N 근무가 특정 인원에게 몰리지 않게 분산합니다."
              onChange={(checked) => onUpdateDraft("evenNights", checked)}
            />
            <ConfigToggle
              checked={draft.notOneNight}
              label="단독 나이트 제한"
              note="N 근무가 하루만 단독으로 끊기지 않게 요청 조건에 반영합니다."
              onChange={(checked) => onUpdateDraft("notOneNight", checked)}
            />
          </div>
        </section>
      </div>
    ),
    "OFF 설정": (
      <div className="space-y-4">
        <section className="space-y-3">
          <h4 className="mb-2 text-sm font-bold text-[#101828]">OFF 기준</h4>
          <NumberValue
            label="월 OFF 수"
            note="단계별 설정에서는 최소 8일부터 조정합니다."
            suffix="일"
            value={draft.offDays}
            variant="staged"
            onChange={(value) =>
              onUpdateDraft("offDays", Math.max(MIN_STAGED_OFF_DAYS, value))
            }
          />
          <NumberValue
            label="연속 OFF 상한 (3-6일)"
            min={MIN_CONSEQ_OFF_DAYS}
            max={MAX_CONSEQ_OFF_DAYS}
            note="해당 설정은 강제하지 않으며, 생성 상황에 따라 변경될 수 있습니다."
            noteTone="caution"
            suffix="일"
            value={draft.maxConseqOff}
            variant="staged"
            onChange={(value) => onUpdateDraft("maxConseqOff", value)}
          />
          <ConfigToggle
            checked={draft.offSwapEnabled}
            label="OFF 초과 시 연차 전환"
            note="OFF가 기준보다 많아질 때 연차로 대체할 수 있게 합니다."
            onChange={(checked) => onUpdateDraft("offSwapEnabled", checked)}
          />
          <ConfigToggle
            checked={draft.healthLeaveEnabled}
            label="보건휴가 자동 부여"
            note="대상자에게 월 1개를 자동 배정합니다. 근무코드 설정에서 보건휴가 대상 코드를 먼저 지정해야 합니다."
            onChange={(checked) => onUpdateDraft("healthLeaveEnabled", checked)}
          />
          {draft.healthLeaveEnabled ? (
            <ConfigToggle
              checked={draft.healthLeaveWeekend}
              label="보건휴가 주말 배치 허용"
              note="끄면 평일에만 배치합니다."
              onChange={(checked) => onUpdateDraft("healthLeaveWeekend", checked)}
            />
          ) : null}
          <ConfigToggle
            checked={draft.sleepOffEnabled}
            label="수면OFF 자동 부여"
            note="나이트 연번이 아래 주기에 도달하면 1개를 부여합니다. 근무코드 설정에서 수면OFF 대상 코드를 먼저 지정해야 합니다."
            onChange={(checked) => onUpdateDraft("sleepOffEnabled", checked)}
          />
          {draft.sleepOffEnabled ? (
            <NumberValue
              label="수면OFF 트리거 주기"
              note="나이트를 이 횟수만큼 서면 수면OFF 대상이 됩니다. 실무 기준은 15입니다."
              suffix="회"
              value={draft.sleepOffCycle}
              variant="staged"
              onChange={(value) => onUpdateDraft("sleepOffCycle", value)}
            />
          ) : null}
          <ConfigToggle
            checked={draft.banNightBeforeFixedWantedOff}
            label="신청 휴무 전날 나이트 금지"
            note="신청해서 확정된 휴무 바로 앞날에는 나이트를 넣지 않습니다. 나이트 뒤 회복 휴무로 신청 휴무가 쓰이는 것을 막습니다. 자동으로 배정된 휴무는 해당하지 않으며, 휴가·공가 앞날은 별도로 이미 금지돼 있습니다."
            onChange={(checked) =>
              onUpdateDraft("banNightBeforeFixedWantedOff", checked)
            }
          />
        </section>
      </div>
    ),
    "근무 설정": (
      <div className="space-y-4">
        <section className="space-y-3">
          <h4 className="mb-2 text-sm font-bold text-[#101828]">근무 기준</h4>
          <NumberValue
            label="연속 근무 상한"
            note="연속으로 배정 가능한 최대 근무 일수입니다."
            suffix="일"
            value={draft.maxConseqWork}
            variant="staged"
            onChange={(value) => onUpdateDraft("maxConseqWork", value)}
          />
          <NumberValue
            label="월 나이트 상한"
            note="한 달에 배정 가능한 최대 N 근무 횟수입니다."
            suffix="회"
            value={draft.maxNightPerMonth}
            variant="staged"
            onChange={(value) => onUpdateDraft("maxNightPerMonth", value)}
          />
          <ConfigToggle
            checked={draft.threeSeqNight}
            label="나이트 3연속 허용"
            note="N 근무 3일 연속 배정을 허용합니다."
            onChange={(checked) => onUpdateDraft("threeSeqNight", checked)}
          />
        </section>
      </div>
    ),
  };

  return (
    <div>
      <section className="space-y-3">
        <SetupModeSwitch
          contextLabel={contextLabel}
          description={
            contextDescription || "현재 설정을 기준으로 단계별 값을 확인합니다."
          }
          disabled={disabled}
          notice={notice}
          onOpenPresets={onOpenPresets}
        />
        <div className=" rounded-xl  border border-[#E4E7EC] bg-white p-6">
          <div>
            <span className="text-xs font-bold text-[#0E59C6]">
              {step + 1}단계
            </span>
            <h3 className="mt-1 text-[21px] font-bold leading-tight text-[#101828]">
              {currentStep}
            </h3>
            <p className="mt-1 text-sm leading-7 text-[#667085]">
              {STAGED_SETUP_STEP_DESCRIPTIONS[currentStep]}
            </p>
          </div>
          <div
            className="my-3 rounded-[18px] bg-[#F8FAFC] px-7 py-5"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={STAGED_SETUP_STEPS.length}
            aria-valuenow={step + 1}
            aria-label="단계별 설정 진행률"
          >
            <div className="relative grid grid-cols-5">
              <span
                className="absolute left-[10%] right-[10%] top-[8px] border-t border-dashed border-[#D0D5DD]"
                aria-hidden="true"
              />
              <span
                className="absolute left-[10%] top-[8px] h-px bg-[#0E59C6]"
                style={{
                  width: `${(Math.min(step, STAGED_SETUP_STEPS.length - 1) / (STAGED_SETUP_STEPS.length - 1)) * 80}%`,
                }}
                aria-hidden="true"
              />
              {STAGED_SETUP_STEPS.map((stageName, index) => (
                <button
                  key={stageName}
                  type="button"
                  disabled={disabled}
                  onClick={() => onStepChange(index)}
                  aria-current={index === step ? "step" : undefined}
                  className="group relative z-10 flex min-w-0 flex-col items-center pb-1 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span
                    className={cn(
                      "relative flex size-4 items-center justify-center rounded-full bg-[#F8FAFC] transition-colors",
                      index === step
                        ? "border-[3px] border-[#8AB7F5]"
                        : index < step
                          ? "border border-[#B8CAE8]"
                          : "border border-transparent"
                    )}
                    aria-hidden="true"
                  >
                    {index === step ? (
                      <span
                        className="absolute inset-[-5px] animate-ping rounded-full border border-[#0E59C6]/65 bg-[#0E59C6]/10"
                        aria-hidden="true"
                      />
                    ) : null}
                    <span
                      className={cn(
                        "rounded-full",
                        index === step
                          ? "size-1.5 bg-[#0E59C6]"
                          : index < step
                            ? "size-2 bg-[#0E59C6]"
                            : "size-2 bg-[#DADDE3]"
                      )}
                    />
                  </span>
                  <span
                    className={cn(
                      "mt-3 block max-w-full truncate text-sm font-bold transition-colors",
                      index === step
                        ? "text-[#0E59C6]"
                        : "text-[#98A2B3] group-hover:text-[#667085]"
                    )}
                  >
                    {stageName}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 min-h-[260px]">
            {stepContentByName[currentStep]}
          </div>
        </div>
      </section>
    </div>
  );
}

function formatSavedAt(value: string | null | undefined) {
  if (!value) return "저장 이력 없음";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

export default function RosterCreateConfigModal({
  disabled,
  isOpen,
  isPending,
  onClose,
  onGenerate,
  pendingMsg = "근무표를 생성 중입니다.",
  selectedGroupId,
  serverConfig: _initialServerConfig,
  workerCount = 0,
}: RosterCreateConfigModalProps) {
  const isAdmin = useIsAdmin();
  const queryClient = useQueryClient();
  const { groups } = useGroupSelection();
  const latestConfigGroupId = isAdmin ? (selectedGroupId ?? "") : "";
  const favoriteStorageKey = savedConfigFavoriteStorageKey(latestConfigGroupId);
  const cachedLatestConfig = queryClient.getQueryData<RosterConfigObject>(
    QUERY_KEYS.config.configData(latestConfigGroupId)
  );
  const { data: fetchedServerConfig } = useQuery({
    queryKey: QUERY_KEYS.config.configData(latestConfigGroupId),
    queryFn: () =>
      httpClient.get<RosterConfigObject>("/roster/config/version/latest", {
        params:
          isAdmin && selectedGroupId
            ? { group_id: selectedGroupId }
            : undefined,
      }),
    enabled: isOpen,
    initialData: cachedLatestConfig,
    staleTime: 60 * 1000,
    refetchOnMount: false,
  });
  const { data: presets = [], isLoading: isPresetListLoading } =
    useRosterConfigVersionsQuery({ enabled: isOpen });
  const saveConfigMutation = useSyncRosterCreateConfigMutation();
  void _initialServerConfig;
  const serverConfig = fetchedServerConfig ?? null;
  const [activeTab, setActiveTab] = useState<"edit" | "presets">("edit");
  const [startMode, setStartMode] = useState<ConfigStartMode>("choose");
  const [setupMode, setSetupMode] = useState<SetupMode | null>(null);
  const [stagedStep, setStagedStep] = useState(0);
  const [isStagedConfirmDialogOpen, setIsStagedConfirmDialogOpen] =
    useState(false);
  const [isStartPresetPickerStackOpen, setIsStartPresetPickerStackOpen] =
    useState(false);
  const [selectedStartSource, setSelectedStartSource] =
    useState<ConfigStartSourceKind>("latest");
  const [selectedStartPreset, setSelectedStartPreset] =
    useState<RosterConfigPreset | null>(null);
  const [selectedEditPreset, setSelectedEditPreset] =
    useState<RosterConfigPreset | null>(null);
  const [baseConfig, setBaseConfig] = useState<RosterConfigObject | null>(null);
  const [draft, setDraft] = useState<ConfigDraft>(DEFAULT_DRAFT);
  const [appliedConfigSignature, setAppliedConfigSignature] = useState("");
  const [presetMeta, setPresetMeta] = useState<PresetMeta>({
    configId: null,
    configVersion: null,
    configName: "",
    configMemo: "",
  });
  const [previewPreset, setPreviewPreset] = useState<RosterConfigPreset | null>(
    null
  );
  const [previewConfig, setPreviewConfig] = useState<RosterConfigObject | null>(
    null
  );
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isReturnSaveChoiceOpen, setIsReturnSaveChoiceOpen] = useState(false);
  const [savePresetForm, setSavePresetForm] = useState<SavePresetForm>({
    configName: "",
    configMemo: "",
  });
  const [isGenerateSaveChoiceOpen, setIsGenerateSaveChoiceOpen] =
    useState(false);
  const [saveTarget, setSaveTarget] = useState<SaveTarget>("new");
  const [pendingReturnAction, setPendingReturnAction] =
    useState<PendingReturnAction>("none");
  const [isPresetLoading, setIsPresetLoading] = useState(false);
  const [deletingPresetId, setDeletingPresetId] = useState<number | null>(null);
  const [appliedPresetMessage, setAppliedPresetMessage] = useState("");
  const wasOpenRef = useRef(false);
  const draftTouchedRef = useRef(false);
  const modalHistoryEntryRef = useRef(false);
  const ignoreNextModalPopRef = useRef(false);
  const savedPresetBaselineSignatureRef = useRef<string | null>(null);
  const savedPickerSurfaceRef = useRef<SavedPickerSurface | null>(null);
  const keepStartPresetPickerOpenRef = useRef(false);
  const stagedStepRef = useRef(0);
  const setupModeRef = useRef<SetupMode | null>(null);
  const startModeRef = useRef<ConfigStartMode>("choose");
  stagedStepRef.current = stagedStep;
  setupModeRef.current = setupMode;
  startModeRef.current = startMode;

  const groupName = useMemo(
    () =>
      groups.find((group) => group.group_id === selectedGroupId)?.group_name ??
      "선택한 병동",
    [groups, selectedGroupId]
  );

  const buildConfigPayload = useCallback(
    (
      sourceConfig: RosterConfigObject | null,
      sourceDraft: ConfigDraft,
      meta: PresetMeta
    ): RosterCreateConfigPayload | null => {
      if (!sourceConfig) return null;
      const { created_at, updated_at, version, ...configBase } = sourceConfig;
      void created_at;
      void updated_at;
      void version;

      return {
        ...configBase,
        config_id: meta.configId,
        config_name: meta.configName.trim() || null,
        config_memo: meta.configMemo.trim() || null,
        off_days: sourceDraft.offDays,
        max_conseq_work: sourceDraft.maxConseqWork,
        max_conseq_off: clampConseqOffDays(sourceDraft.maxConseqOff),
        max_nig_per_month: sourceDraft.maxNightPerMonth,
        three_seq_nig: sourceDraft.threeSeqNight,
        weekly_off_group: sourceDraft.weeklyOffGroup,
        off_placement_mode: sourceDraft.offPlacementMode,
        off_swap_enabled: sourceDraft.offSwapEnabled,
        health_leave_enabled: sourceDraft.healthLeaveEnabled,
        health_leave_weekend: sourceDraft.healthLeaveWeekend,
        sleep_off_enabled: sourceDraft.sleepOffEnabled,
        sleep_off_cycle: sourceDraft.sleepOffCycle,
        ban_night_before_fixed_wanted_off:
          sourceDraft.banNightBeforeFixedWantedOff,
        fixed_wanted_use_yn: sourceDraft.fixedWantedUse,
        preceptee_on: sourceDraft.precepteeOn,
        preceptee_shift_count: sourceDraft.precepteeShiftCount,
        off_first: sourceDraft.offFirst,
        even_nights: sourceDraft.evenNights,
        not_one_night: sourceDraft.notOneNight,
      };
    },
    []
  );

  const buildGenerationPayloadFromDraft = useCallback(
    (
      sourceDraft: ConfigDraft,
      disposition: GenerateConfigDisposition = "auto"
    ): RosterCreateConfigPayload | null => {
      const shouldCreateNewConfig =
        disposition === "create-new" ||
        startMode === "new" ||
        (disposition === "auto" &&
          startMode === "saved" &&
          savedPresetBaselineSignatureRef.current !== null &&
          configConditionSignature(
            buildConfigPayload(baseConfig, sourceDraft, presetMeta)
          ) !== savedPresetBaselineSignatureRef.current);
      const generationMeta = shouldCreateNewConfig
        ? {
            configId: null,
            configVersion: null,
            configName: presetMeta.configName,
            configMemo: presetMeta.configMemo,
          }
        : presetMeta;

      return buildConfigPayload(baseConfig, sourceDraft, generationMeta);
    },
    [baseConfig, buildConfigPayload, presetMeta, startMode]
  );

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (!isOpen) {
      draftTouchedRef.current = false;
      savedPresetBaselineSignatureRef.current = null;
      setIsGenerateSaveChoiceOpen(false);
      setIsReturnSaveChoiceOpen(false);
      setActiveTab("edit");
      setStartMode("choose");
      setSetupMode(null);
      setStagedStep(0);
      setIsStagedConfirmDialogOpen(false);
      setSelectedStartSource("latest");
      setSelectedStartPreset(null);
      keepStartPresetPickerOpenRef.current = false;
      setIsStartPresetPickerStackOpen(false);
      setPendingReturnAction("none");
      return;
    }

    if (justOpened) {
      draftTouchedRef.current = false;
    } else if (draftTouchedRef.current) {
      return;
    }

    if (startMode !== "choose") {
      return;
    }

    if (serverConfig) {
      const nextSignature = configDraftSignature(serverConfig);
      setBaseConfig(serverConfig);
      setPresetMeta({
        configId: serverConfig.config_id ?? null,
        configVersion: serverConfig.version ?? null,
        configName: serverConfig.config_name ?? "",
        configMemo: serverConfig.config_memo ?? "",
      });
      setPreviewPreset(null);
      setPreviewConfig(null);
      setDraft(toDraft(serverConfig));
      setAppliedConfigSignature(nextSignature);
      setSelectedStartSource("latest");
      setSelectedStartPreset(null);
    }
  }, [isOpen, serverConfig, startMode]);

  const pushModalHistoryEntry = useCallback(() => {
    window.history.pushState(
      {
        ...(typeof window.history.state === "object" &&
        window.history.state !== null
          ? window.history.state
          : {}),
        rosterCreateConfigModal: true,
      },
      "",
      window.location.href
    );
    modalHistoryEntryRef.current = true;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      modalHistoryEntryRef.current = false;
      ignoreNextModalPopRef.current = false;
      return;
    }

    if (!modalHistoryEntryRef.current) {
      pushModalHistoryEntry();
    }

    const handlePopState = () => {
      if (ignoreNextModalPopRef.current) {
        ignoreNextModalPopRef.current = false;
        return;
      }

      if (!modalHistoryEntryRef.current) {
        return;
      }

      if (activeTab === "presets") {
        setActiveTab("edit");
        pushModalHistoryEntry();
        return;
      }

      if (isGenerateSaveChoiceOpen) {
        setIsGenerateSaveChoiceOpen(false);
        pushModalHistoryEntry();
        return;
      }

      if (isReturnSaveChoiceOpen) {
        setIsReturnSaveChoiceOpen(false);
        setPendingReturnAction("none");
        pushModalHistoryEntry();
        return;
      }

      if (isSaveDialogOpen) {
        setIsSaveDialogOpen(false);
        pushModalHistoryEntry();
        return;
      }

      if (
        startModeRef.current !== "choose" &&
        setupModeRef.current === "staged" &&
        stagedStepRef.current > 0
      ) {
        const nextStep = Math.max(0, stagedStepRef.current - 1);
        stagedStepRef.current = nextStep;
        setStagedStep(nextStep);
        return;
      }

      if (startModeRef.current !== "choose") {
        const currentPayload = buildConfigPayload(
          baseConfig,
          draft,
          presetMeta
        );
        const hasChangedConfig =
          draftTouchedRef.current &&
          (startModeRef.current === "new" ||
            (startModeRef.current === "saved" &&
              savedPresetBaselineSignatureRef.current !== null &&
              configConditionSignature(currentPayload) !==
                savedPresetBaselineSignatureRef.current));

        if (hasChangedConfig) {
          setPendingReturnAction("start");
          setIsReturnSaveChoiceOpen(true);
          pushModalHistoryEntry();
          return;
        }

        draftTouchedRef.current = false;
        savedPresetBaselineSignatureRef.current = null;
        setIsGenerateSaveChoiceOpen(false);
        setActiveTab("edit");
        setSetupMode(null);
        setStagedStep(0);
        setSelectedStartSource("latest");
        setSelectedStartPreset(null);
        setStartMode("choose");
        pushModalHistoryEntry();
        return;
      }

      modalHistoryEntryRef.current = false;
      onClose();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [
    activeTab,
    baseConfig,
    buildConfigPayload,
    draft,
    isGenerateSaveChoiceOpen,
    isOpen,
    isReturnSaveChoiceOpen,
    isSaveDialogOpen,
    onClose,
    presetMeta,
    pushModalHistoryEntry,
    setupMode,
    startMode,
  ]);

  const canGenerate =
    Boolean(baseConfig) &&
    (isStagedConfirmDialogOpen ||
      (startMode !== "choose" && setupMode !== null));
  const isDeletingPreset = deletingPresetId !== null;
  const isSavingPreset = saveConfigMutation.isPending;
  const showsBlockingOverlay = isPending || isDeletingPreset;
  const isBusy = showsBlockingOverlay || isPresetLoading || isSavingPreset;
  const serverConfigSignature = configDraftSignature(serverConfig);
  const isInitialConfigLoading =
    startMode === "choose" &&
    selectedStartSource !== "preset" &&
    !draftTouchedRef.current &&
    (serverConfigSignature === "" ||
      appliedConfigSignature !== serverConfigSignature);
  const hasSavedDraftChanges = useMemo(() => {
    if (startMode !== "saved") return false;
    const baseline = savedPresetBaselineSignatureRef.current;
    if (baseline === null) return false;
    const currentPayload = buildConfigPayload(baseConfig, draft, presetMeta);
    return configConditionSignature(currentPayload) !== baseline;
  }, [baseConfig, buildConfigPayload, draft, presetMeta, startMode]);
  const hasReturnDraftChanges =
    startMode !== "choose" &&
    draftTouchedRef.current &&
    (startMode === "new" || hasSavedDraftChanges);
  const summary = useMemo(
    () =>
      [
        `OFF ${draft.offDays}일`,
        `연속 OFF ${draft.maxConseqOff}일`,
        `연속 근무 ${draft.maxConseqWork}일`,
        `월 N ${draft.maxNightPerMonth}회`,
        draft.fixedWantedUse ? "원티드 반영" : "원티드 미반영",
        draft.precepteeShiftCount ? "프리셉티 포함" : "프리셉티 제외",
      ].join(" · "),
    [draft]
  );

  const setupContextLabel =
    startMode === "new"
      ? "새 설정"
      : presetMeta.configName ||
        baseConfig?.config_name ||
        serverConfig?.config_name ||
        (presetMeta.configVersion !== null
          ? `설정 v${presetMeta.configVersion}`
          : baseConfig?.version !== null && baseConfig?.version !== undefined
            ? `설정 v${baseConfig.version}`
            : serverConfig?.version !== null &&
                serverConfig?.version !== undefined
              ? `설정 v${serverConfig.version}`
              : "최근 설정");
  const setupContextDescription =
    presetMeta.configMemo.trim() ||
    baseConfig?.config_memo?.trim() ||
    serverConfig?.config_memo?.trim() ||
    "";
  const setupChangeNotice =
    startMode === "saved" && hasSavedDraftChanges ? (
      <span className="shrink-0 rounded-full bg-[#FFF7E6] px-2 py-0.5 text-[11px] font-bold text-[#B54708]">
        근무 조건 변경됨
      </span>
    ) : null;

  const updateDraft = <K extends keyof ConfigDraft>(
    key: K,
    value: ConfigDraft[K]
  ) => {
    draftTouchedRef.current = true;
    setAppliedPresetMessage("");
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const startNewConfig = (mode: SetupMode) => {
    const seedConfig = serverConfig ?? baseConfig;
    if (!seedConfig) {
      toast.error("근무표 설정을 불러온 뒤 다시 시도해주세요.");
      return;
    }

    setBaseConfig(seedConfig);
    setPresetMeta({
      configId: null,
      configVersion: null,
      configName: "",
      configMemo: "",
    });
    savedPresetBaselineSignatureRef.current = null;
    setPreviewPreset(null);
    setPreviewConfig(null);
    setAppliedPresetMessage("");
    setDraft(DEFAULT_DRAFT);
    draftTouchedRef.current = true;
    setStartMode("new");
    setSetupMode(mode);
    setStagedStep(0);
    setIsStagedConfirmDialogOpen(false);
    setActiveTab("edit");
  };

  const startLatestConfig = (mode: SetupMode) => {
    const seedConfig = serverConfig ?? baseConfig;
    if (!seedConfig) {
      toast.error("최근 설정을 불러온 뒤 다시 시도해주세요.");
      return;
    }

    const nextDraft = toDraft(seedConfig);
    const nextMeta = {
      configId: seedConfig.config_id ?? null,
      configVersion: seedConfig.version ?? null,
      configName: seedConfig.config_name ?? "",
      configMemo: seedConfig.config_memo ?? "",
    };
    setBaseConfig(seedConfig);
    setPresetMeta(nextMeta);
    setPreviewPreset(null);
    setPreviewConfig(null);
    setAppliedPresetMessage("");
    setDraft(nextDraft);
    setAppliedConfigSignature(configDraftSignature(seedConfig));
    savedPresetBaselineSignatureRef.current =
      seedConfig.version !== null && seedConfig.version !== undefined
        ? configConditionSignature(
            buildConfigPayload(seedConfig, nextDraft, nextMeta)
          )
        : null;
    draftTouchedRef.current = false;
    setStartMode(
      seedConfig.version !== null && seedConfig.version !== undefined
        ? "saved"
        : "new"
    );
    setSetupMode(mode);
    setStagedStep(0);
    setIsStagedConfirmDialogOpen(false);
    setActiveTab("edit");
  };

  const invalidateConfigQueries = useCallback(async () => {
    const queryGroupId = isAdmin ? (selectedGroupId ?? "") : "";
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configData(queryGroupId),
      }),
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configVersions(queryGroupId),
      }),
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.rosterConfigVersions(queryGroupId),
      }),
    ]);
  }, [isAdmin, queryClient, selectedGroupId]);

  const updatePresetVersionsCache = useCallback(
    (
      updater: (
        presets: RosterConfigVersionsResponse
      ) => RosterConfigVersionsResponse
    ) => {
      const queryGroupId = isAdmin ? (selectedGroupId ?? "") : "";
      queryClient.setQueryData<RosterConfigVersionsResponse>(
        QUERY_KEYS.config.rosterConfigVersions(queryGroupId),
        (currentPresets) =>
          currentPresets ? updater(currentPresets) : currentPresets
      );
    },
    [isAdmin, queryClient, selectedGroupId]
  );

  const patchCurrentConfigMeta = useCallback(
    ({
      configId,
      configMemo,
      configName,
      version,
    }: {
      configId: number | null;
      configMemo: string | null;
      configName: string | null;
      version: number | null;
    }) => {
      if (configId === null || presetMeta.configId !== configId) return;

      setPresetMeta((prev) => ({
        ...prev,
        configMemo: configMemo ?? "",
        configName: configName ?? "",
        configVersion: version ?? prev.configVersion,
      }));
      setBaseConfig((prev) => {
        if (!prev || prev.config_id !== configId) return prev;
        const nextConfig = {
          ...prev,
          config_memo: configMemo,
          config_name: configName,
          version: version ?? prev.version,
        };
        queryClient.setQueryData(
          QUERY_KEYS.config.configData(latestConfigGroupId),
          nextConfig
        );
        return nextConfig;
      });
    },
    [latestConfigGroupId, presetMeta.configId, queryClient]
  );

  const closeConfigModal = useCallback(() => {
    if (
      isBusy ||
      activeTab === "presets" ||
      isGenerateSaveChoiceOpen ||
      isReturnSaveChoiceOpen ||
      isSaveDialogOpen
    ) {
      return;
    }

    if (modalHistoryEntryRef.current) {
      ignoreNextModalPopRef.current = true;
      modalHistoryEntryRef.current = false;
      window.history.back();
    }

    onClose();
  }, [
    activeTab,
    isBusy,
    isGenerateSaveChoiceOpen,
    isReturnSaveChoiceOpen,
    isSaveDialogOpen,
    onClose,
  ]);

  if (!isOpen) return null;

  const fetchPresetConfig = async (preset: RosterConfigPreset) => {
    return httpClient.get<RosterConfigObject>(
      `/roster/config/version/${preset.version}`,
      {
        params:
          isAdmin && selectedGroupId
            ? { group_id: selectedGroupId }
            : undefined,
      }
    );
  };

  const handleLoadPresetFromStart = async (
    preset: RosterConfigPreset,
    mode: SetupMode,
    nextAction: "open-setup" | "confirm-generate" = "open-setup",
    options?: { keepStartPickerOpen?: boolean }
  ) => {
    const keepStartPickerOpen =
      options?.keepStartPickerOpen ?? nextAction === "confirm-generate";
    setIsPresetLoading(true);
    try {
      const config = await fetchPresetConfig(preset);
      const appliedName = config.config_name ?? preset.config_name ?? "";
      const nextDraft = toDraft(config);
      const nextMeta = {
        configId: config.config_id,
        configVersion: preset.version,
        configName: appliedName,
        configMemo: config.config_memo ?? preset.config_memo ?? "",
      };
      setBaseConfig(config);
      setPresetMeta(nextMeta);
      setPreviewPreset(null);
      setPreviewConfig(null);
      setAppliedPresetMessage("");
      setDraft(nextDraft);
      setAppliedConfigSignature(configDraftSignature(config));
      savedPresetBaselineSignatureRef.current = configConditionSignature(
        buildConfigPayload(config, nextDraft, nextMeta)
      );
      draftTouchedRef.current = false;
      setSelectedStartSource("preset");
      setSelectedStartPreset(preset);
      if (nextAction === "confirm-generate") {
        setIsStartPresetPickerStackOpen(keepStartPickerOpen);
        setActiveTab("edit");
        setIsStagedConfirmDialogOpen(true);
        if (!keepStartPickerOpen) return;
        window.setTimeout(() => {
          if (!keepStartPresetPickerOpenRef.current) return;
          setIsStartPresetPickerStackOpen(true);
          setSelectedStartSource("preset");
          setSelectedStartPreset(preset);
        }, 0);
        return;
      }
      setStartMode("saved");
      setSetupMode(mode);
      setStagedStep(0);
      setIsStagedConfirmDialogOpen(false);
      setActiveTab("edit");
    } catch (error) {
      if (nextAction === "confirm-generate") {
        keepStartPresetPickerOpenRef.current = false;
      }
      toast.error(
        error instanceof Error
          ? error.message
          : "저장된 설정을 불러오지 못했습니다."
      );
    } finally {
      setIsPresetLoading(false);
    }
  };

  const handleSelectStartPreset = (preset: RosterConfigPreset) => {
    setIsStartPresetPickerStackOpen(true);
    setSelectedStartSource("preset");
    setSelectedStartPreset(preset);
  };

  const handleShowStartPresets = () => {
    setIsStartPresetPickerStackOpen(true);
    setSelectedStartSource("preset");
    setSelectedStartPreset(null);
  };

  const handleHideStartPresets = () => {
    if (keepStartPresetPickerOpenRef.current) return;
    setIsStartPresetPickerStackOpen(false);
    setSelectedStartSource("latest");
    setSelectedStartPreset(null);
  };

  const getOpenSavedPickerSurface = (): SavedPickerSurface | null => {
    if (startModeRef.current === "choose" && selectedStartSource === "preset") {
      return "start";
    }
    if (activeTab === "presets") {
      return "edit";
    }
    return null;
  };

  const restoreSavedPickerSurface = (surface: SavedPickerSurface | null) => {
    if (surface === "start") {
      setSelectedStartSource("preset");
      return;
    }
    if (surface === "edit") {
      setActiveTab("presets");
    }
  };

  const handleStartSetup = (
    mode: SetupMode,
    presetOverride?: RosterConfigPreset
  ) => {
    if (presetOverride) {
      setSelectedStartSource("preset");
      setSelectedStartPreset(presetOverride);
      void handleLoadPresetFromStart(presetOverride, mode);
      return;
    }

    if (selectedStartSource === "new") {
      startNewConfig(mode);
      return;
    }

    if (selectedStartSource === "preset" && selectedStartPreset) {
      void handleLoadPresetFromStart(selectedStartPreset, mode);
      return;
    }

    startLatestConfig(mode);
  };

  const handleShowEditPresets = () => {
    setSelectedEditPreset(null);
    setActiveTab("presets");
  };

  const handleUseEditPreset = (
    mode: SetupMode,
    presetOverride?: RosterConfigPreset
  ) => {
    const preset = presetOverride ?? selectedEditPreset;
    if (!preset) return;
    setSelectedEditPreset(preset);

    if (preset.config_id === presetMeta.configId) {
      setActiveTab("edit");
      setSetupMode(mode);
      if (mode === "staged") {
        setStagedStep(0);
      }
      return;
    }

    void handleLoadPresetFromStart(preset, mode);
  };

  const handleGenerateFromEditPreset = (
    presetOverride?: RosterConfigPreset
  ) => {
    const preset = presetOverride ?? selectedEditPreset;
    if (!preset) return;
    setSelectedEditPreset(preset);
    keepStartPresetPickerOpenRef.current = false;

    if (preset.config_id === presetMeta.configId) {
      setActiveTab("edit");
      setIsStagedConfirmDialogOpen(true);
      return;
    }

    void handleLoadPresetFromStart(
      preset,
      setupMode ?? "staged",
      "confirm-generate",
      { keepStartPickerOpen: false }
    );
  };

  const handleStartGenerateFromPreset = (
    presetOverride?: RosterConfigPreset
  ) => {
    const preset = presetOverride ?? selectedStartPreset;
    if (!preset) return;
    keepStartPresetPickerOpenRef.current = true;
    setIsStartPresetPickerStackOpen(true);
    setSelectedStartSource("preset");
    setSelectedStartPreset(preset);
    void handleLoadPresetFromStart(preset, "staged", "confirm-generate");
  };

  const handleChangeSetupMode = (mode: SetupMode) => {
    setSetupMode(mode);
    setIsStagedConfirmDialogOpen(false);
    if (mode === "staged") {
      setStagedStep(0);
    }
  };

  const handleStagedStepChange = (nextStep: number) => {
    setIsStagedConfirmDialogOpen(false);
    const boundedStep = Math.min(
      STAGED_SETUP_STEPS.length - 1,
      Math.max(0, nextStep)
    );
    if (
      setupMode === "staged" &&
      startMode !== "choose" &&
      boundedStep > stagedStep
    ) {
      window.history.pushState(
        {
          ...(typeof window.history.state === "object" &&
          window.history.state !== null
            ? window.history.state
            : {}),
          rosterCreateConfigModal: true,
          rosterCreateConfigStep: boundedStep,
        },
        "",
        window.location.href
      );
      modalHistoryEntryRef.current = true;
    }
    stagedStepRef.current = boundedStep;
    setStagedStep(boundedStep);
  };

  const returnToStartChooser = () => {
    draftTouchedRef.current = false;
    savedPresetBaselineSignatureRef.current = null;
    setIsGenerateSaveChoiceOpen(false);
    setIsReturnSaveChoiceOpen(false);
    setPendingReturnAction("none");
    setActiveTab("edit");
    setSetupMode(null);
    setStagedStep(0);
    setIsStagedConfirmDialogOpen(false);
    setSelectedStartSource("latest");
    setSelectedStartPreset(null);
    setStartMode("choose");
  };

  const requestReturnToStartChooser = () => {
    if (isBusy) return;

    if (hasReturnDraftChanges) {
      setPendingReturnAction("start");
      setIsReturnSaveChoiceOpen(true);
      return;
    }

    returnToStartChooser();
  };

  const saveCurrentConfigForReturn = async () => {
    const payload = buildGenerationPayloadFromDraft(
      draft,
      presetMeta.configId !== null ? "update-existing" : "create-new"
    );
    if (!payload) {
      toast.error("저장할 설정 정보를 불러오지 못했습니다.");
      return;
    }

    try {
      const result = await saveConfigMutation.mutateAsync({
        configPayload: payload,
        isAdmin,
        selectedGroupId,
      });
      const savedPayload = {
        ...payload,
        config_id: result.configId ?? payload.config_id ?? null,
        config_name: result.configName ?? payload.config_name ?? null,
        config_memo: result.configMemo ?? payload.config_memo ?? null,
      };
      setPresetMeta({
        configId: savedPayload.config_id ?? null,
        configVersion: result.version,
        configName: savedPayload.config_name ?? "",
        configMemo: savedPayload.config_memo ?? "",
      });
      applyGeneratedPayloadLocally(savedPayload);
      await invalidateConfigQueries();
      savedPresetBaselineSignatureRef.current =
        configConditionSignature(savedPayload);
      toast.success("설정을 저장했습니다.");
      returnToStartChooser();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "설정 저장에 실패했습니다."
      );
    }
  };

  const handleDeletePreset = async (preset: RosterConfigPreset) => {
    if (deletingPresetId !== null) return;
    const pickerSurface = getOpenSavedPickerSurface();
    const presetName = preset.config_name || `설정 v${preset.version}`;
    const confirmed = window.confirm(
      `"${presetName}" 저장 설정을 삭제할까요?\n삭제 후에도 이 설정으로 생성한 근무표 이력은 유지됩니다.`
    );
    if (!confirmed) return;

    setDeletingPresetId(preset.config_id);
    try {
      await deleteRosterConfigPresetApi({
        configId: preset.config_id,
        groupId: isAdmin && selectedGroupId ? selectedGroupId : undefined,
      });
      updatePresetVersionsCache((currentPresets) =>
        currentPresets.filter(
          (currentPreset) => currentPreset.config_id !== preset.config_id
        )
      );

      if (previewPreset?.config_id === preset.config_id) {
        setPreviewPreset(null);
        setPreviewConfig(null);
      }
      if (selectedStartPreset?.config_id === preset.config_id) {
        setSelectedStartPreset(null);
      }

      if (presetMeta.configId === preset.config_id) {
        setBaseConfig((prev) =>
          prev?.config_id === preset.config_id
            ? { ...prev, version: null }
            : prev
        );
        setPresetMeta((prev) => ({
          ...prev,
          configVersion: null,
        }));
        savedPresetBaselineSignatureRef.current = null;
        setAppliedPresetMessage(
          "저장 설정을 삭제했습니다. 현재 편집값은 일반 설정으로 유지됩니다."
        );
      }

      toast.success("저장한 설정을 삭제했습니다.");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "저장한 설정 삭제에 실패했습니다."
      );
    } finally {
      setDeletingPresetId(null);
      restoreSavedPickerSurface(pickerSurface);
    }
  };

  const handleOpenSaveDialog = (target: SaveTarget = "new") => {
    const targetName =
      target === "preview"
        ? (previewPreset?.config_name ?? "")
        : target === "current"
          ? presetMeta.configName
          : "";
    const targetMemo =
      target === "preview"
        ? (previewPreset?.config_memo ?? "")
        : target === "current"
          ? presetMeta.configMemo
          : "";

    setSaveTarget(target);
    setSavePresetForm({
      configName: targetName,
      configMemo: targetMemo,
    });
    setIsSaveDialogOpen(true);
  };

  const handleEditPresetMetaFromStart = (preset: RosterConfigPreset) => {
    savedPickerSurfaceRef.current = getOpenSavedPickerSurface();
    setPreviewPreset(preset);
    setPreviewConfig(null);
    setSaveTarget("preview");
    setSavePresetForm({
      configName: preset.config_name ?? "",
      configMemo: preset.config_memo ?? "",
    });
    setIsSaveDialogOpen(true);
  };

  const handleSavePreset = async () => {
    const nextName = savePresetForm.configName.trim();
    if (!nextName) {
      toast.error("설정 이름을 입력해주세요.");
      return;
    }

    try {
      const isPreviewSave = saveTarget === "preview" && previewPreset;
      const isNewPresetSave = saveTarget === "new";
      const targetPreviewConfig =
        isPreviewSave && previewPreset
          ? (previewConfig ?? (await fetchPresetConfig(previewPreset)))
          : null;
      const payload = isPreviewSave
        ? buildConfigPayload(
            targetPreviewConfig,
            toDraft(targetPreviewConfig),
            {
              configId:
                targetPreviewConfig?.config_id ?? previewPreset.config_id,
              configVersion: previewPreset.version,
              configName:
                targetPreviewConfig?.config_name ??
                previewPreset.config_name ??
                "",
              configMemo:
                targetPreviewConfig?.config_memo ??
                previewPreset.config_memo ??
                "",
            }
          )
        : buildConfigPayload(
            baseConfig,
            isNewPresetSave ? draft : toDraft(baseConfig),
            {
              configId: isNewPresetSave ? null : presetMeta.configId,
              configVersion: isNewPresetSave ? null : presetMeta.configVersion,
              configName: "",
              configMemo: "",
            }
          );
      if (!payload) {
        toast.error("저장할 설정 정보를 불러오지 못했습니다.");
        return;
      }

      const savePayload = {
        ...payload,
        config_name: nextName,
        config_memo: savePresetForm.configMemo.trim() || null,
      };

      const result = await saveConfigMutation.mutateAsync({
        configPayload: savePayload,
        isAdmin,
        selectedGroupId,
      });
      const savedConfigId =
        result.configId ??
        savePayload.config_id ??
        (isPreviewSave ? previewPreset.config_id : null);
      const savedConfigName =
        result.configName ?? savePayload.config_name ?? "";
      const savedConfigMemo =
        result.configMemo ?? savePayload.config_memo ?? null;
      if (savedConfigId !== null) {
        updatePresetVersionsCache((currentPresets) =>
          currentPresets.map((currentPreset) =>
            currentPreset.config_id === savedConfigId
              ? {
                  ...currentPreset,
                  config_memo: savedConfigMemo,
                  config_name: savedConfigName,
                  version: result.version ?? currentPreset.version,
                }
              : currentPreset
          )
        );
        patchCurrentConfigMeta({
          configId: savedConfigId,
          configMemo: savedConfigMemo,
          configName: savedConfigName,
          version: result.version,
        });
      }
      if (isPreviewSave) {
        setPreviewPreset((prev) =>
          prev
            ? {
                ...prev,
                config_name: savedConfigName,
                config_memo: savedConfigMemo,
                version: result.version ?? prev.version,
              }
            : prev
        );
        setPreviewConfig((prev) =>
          prev
            ? {
                ...prev,
                config_name: savedConfigName,
                config_memo: savedConfigMemo,
                version: result.version ?? prev.version,
              }
            : prev
        );
      } else {
        setPresetMeta({
          configId: result.configId,
          configVersion: result.version,
          configName: savedConfigName,
          configMemo: savedConfigMemo ?? "",
        });
      }
      setIsSaveDialogOpen(false);
      restoreSavedPickerSurface(savedPickerSurfaceRef.current);
      savedPickerSurfaceRef.current = null;
      toast.success(
        isPreviewSave ? "설명 정보를 수정했습니다." : "설정을 저장했습니다."
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "설정 저장에 실패했습니다."
      );
    }
  };

  const applyGeneratedPayloadLocally = (payload: RosterCreateConfigPayload) => {
    const nextConfig = mergeConfigPayloadForCache(baseConfig, payload);
    if (!nextConfig) return;

    queryClient.setQueryData(
      QUERY_KEYS.config.configData(latestConfigGroupId),
      nextConfig
    );
    setBaseConfig(nextConfig);
    setDraft(toDraft(nextConfig));
    setAppliedConfigSignature(configDraftSignature(nextConfig));
  };

  const submitGenerate = async (
    disposition: GenerateConfigDisposition = "auto"
  ) => {
    const payload = buildGenerationPayloadFromDraft(draft, disposition);
    if (!payload) {
      toast.error("근무표 설정을 불러온 뒤 다시 시도해주세요.");
      return;
    }

    if (disposition === "update-existing") {
      try {
        const result = await saveConfigMutation.mutateAsync({
          configPayload: payload,
          isAdmin,
          selectedGroupId,
        });
        const savedPayload = {
          ...payload,
          config_id: result.configId ?? payload.config_id ?? null,
          config_name: result.configName ?? payload.config_name ?? null,
          config_memo: result.configMemo ?? payload.config_memo ?? null,
        };
        setPresetMeta({
          configId: savedPayload.config_id ?? null,
          configVersion: result.version,
          configName: savedPayload.config_name ?? "",
          configMemo: savedPayload.config_memo ?? "",
        });
        await invalidateConfigQueries();
        setIsGenerateSaveChoiceOpen(false);
        applyGeneratedPayloadLocally(savedPayload);
        savedPresetBaselineSignatureRef.current =
          configConditionSignature(savedPayload);
        draftTouchedRef.current = false;
        onGenerate(savedPayload);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "기존 설정 업데이트에 실패했습니다."
        );
      }
      return;
    }

    setIsGenerateSaveChoiceOpen(false);
    applyGeneratedPayloadLocally(payload);
    onGenerate(payload);
  };

  const handleGenerate = () => {
    if (
      startMode === "saved" &&
      hasSavedDraftChanges &&
      presetMeta.configId !== null
    ) {
      setIsGenerateSaveChoiceOpen(true);
      return;
    }

    void submitGenerate("auto");
  };
  const handleStagedConfirmGenerate = () => {
    keepStartPresetPickerOpenRef.current = false;
    setIsStartPresetPickerStackOpen(false);
    setIsStagedConfirmDialogOpen(false);
    if (
      startMode === "saved" &&
      hasSavedDraftChanges &&
      presetMeta.configId !== null
    ) {
      setIsGenerateSaveChoiceOpen(true);
      return;
    }

    void submitGenerate("auto");
  };
  const isStagedSetupActive = startMode !== "choose" && setupMode === "staged";
  const isLastStagedStep = stagedStep === STAGED_SETUP_STEPS.length - 1;
  const handleStagedPrevious = () => {
    if (stagedStep <= 0) return;
    handleStagedStepChange(stagedStep - 1);
  };
  const handleStagedNext = () => {
    handleStagedStepChange(stagedStep + 1);
  };
  const handleStagedPrimaryAction = () => {
    if (!isLastStagedStep) {
      handleStagedNext();
      return;
    }

    setIsStagedConfirmDialogOpen(true);
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (open) return;
          closeConfigModal();
        }}
      >
        <DialogContent
          overlayClassName="bg-black/45"
          className={cn(
            "max-h-[calc(100dvh-32px)] max-w-none gap-0 overflow-hidden rounded-[10px] border-0 bg-white p-0 font-pretendard shadow-lg",
            startMode === "choose"
              ? "w-[min(720px,calc(100vw-40px))]"
              : setupMode === "full"
                ? "w-[min(960px,calc(100vw-48px))]"
                : "w-[min(820px,calc(100vw-48px))]"
          )}
        >
          <DialogTitle className="sr-only">근무표 만들기 설정</DialogTitle>
          <DialogDescription className="sr-only">
            설정을 불러온 뒤 단계별 설정 또는 전체 설정으로 근무표 생성 조건을
            편집합니다.
          </DialogDescription>
          <div className="relative flex max-h-[calc(100dvh-32px)] min-h-0 flex-col overflow-hidden bg-white text-[#101828]">
            <header
              className={cn(
                "shrink-0 pr-12",
                setupMode === "staged" ? "px-6 pb-2 pt-4" : "px-7 pb-3 pt-5"
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                {startMode !== "choose" && setupMode !== null && (
                  <button
                    type="button"
                    aria-label="설정 방식 선택으로 돌아가기"
                    disabled={isBusy}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      requestReturnToStartChooser();
                    }}
                    className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-[#E4E7EC] bg-white text-[#667085] transition-colors hover:border-[#B8CAE8] hover:bg-[#F8FAFC] hover:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ArrowLeft className="size-4" aria-hidden="true" />
                  </button>
                )}
                <p className="min-w-0 text-sm font-semibold text-[#0E59C6]">
                  AIDE 생성 도우미
                </p>
              </div>
              {startMode !== "choose" && setupMode !== null && (
                <div className="absolute right-14 top-5">
                  <SetupModeIconToggle
                    disabled={isBusy}
                    mode={setupMode}
                    onChange={handleChangeSetupMode}
                  />
                </div>
              )}
              <p
                className={cn(
                  "mt-1.5 text-[#667085]",
                  setupMode === "staged" ? "text-sm" : "text-sm"
                )}
              >
                새 설정으로 시작하거나 저장된 설정을 불러온 뒤 이번 생성에
                적용할 값을 편집합니다.
              </p>
            </header>

            <div className="h-px shrink-0 bg-[#E4E7EC]" />

            <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FAFC] px-7 py-5">
              {isInitialConfigLoading ? (
                <ConfigModalSkeleton />
              ) : startMode === "choose" ? (
                <ConfigStartChooser
                  deletingPresetId={deletingPresetId}
                  disabled={isBusy}
                  favoriteStorageKey={favoriteStorageKey}
                  isPresetPickerStackOpen={isStartPresetPickerStackOpen}
                  isPresetListLoading={isPresetListLoading}
                  isPresetLoading={isPresetLoading}
                  onDeletePreset={handleDeletePreset}
                  onEditPresetMeta={handleEditPresetMetaFromStart}
                  onHidePresets={handleHideStartPresets}
                  onSelectPreset={handleSelectStartPreset}
                  onShowPresets={handleShowStartPresets}
                  onStartGenerate={handleStartGenerateFromPreset}
                  onStartLatestSetup={startLatestConfig}
                  onStartSetup={handleStartSetup}
                  presets={presets}
                  selectedPreset={selectedStartPreset}
                  selectedSource={selectedStartSource}
                  sourceConfig={serverConfig ?? baseConfig}
                />
              ) : (
                <>
                  {setupMode !== "staged" && (
                    <SetupModeSwitch
                      contextLabel={setupContextLabel}
                      description={
                        setupContextDescription ||
                        "현재 설정을 기준으로 전체 조건을 확인합니다."
                      }
                      disabled={isBusy}
                      notice={setupChangeNotice}
                      onOpenPresets={handleShowEditPresets}
                    />
                  )}
                  {setupMode === "staged" ? (
                    <StagedSetupPanel
                      contextDescription={setupContextDescription}
                      contextLabel={setupContextLabel}
                      disabled={disabled || isBusy || !canGenerate}
                      draft={draft}
                      groupName={groupName}
                      notice={setupChangeNotice}
                      onOpenPresets={handleShowEditPresets}
                      onStepChange={handleStagedStepChange}
                      onUpdateDraft={updateDraft}
                      step={stagedStep}
                      workerCount={workerCount}
                    />
                  ) : (
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                      <ConfigCard
                        badge="1단계"
                        className="lg:col-span-2"
                        description="생성에서 가장 먼저 적용할 배정 방향을 고릅니다."
                        title="기준 선택"
                      >
                        <div className="grid gap-3 md:grid-cols-2">
                          {[
                            {
                              description:
                                "일자별 필요 인원을 먼저 맞추고 남는 칸을 OFF로 회수합니다.",
                              label: "근무 우선",
                              value: true,
                            },
                            {
                              description:
                                "월 OFF 기준을 먼저 맞추고 남는 칸을 근무로 배정합니다.",
                              label: "OFF 균형",
                              value: false,
                            },
                          ].map((option) => (
                            <button
                              key={option.label}
                              type="button"
                              aria-pressed={draft.offFirst === option.value}
                              onClick={() =>
                                updateDraft("offFirst", option.value)
                              }
                              className={cn(
                                "group min-h-[116px] rounded-xl border px-4 py-3.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30",
                                draft.offFirst === option.value
                                  ? "border-[#0E59C6] bg-white shadow-[0px_0px_15px_#0072ff70]"
                                  : "border-[#E4E7EC] bg-white hover:border-[#0E59C6]/40 hover:bg-[#F8FAFC]"
                              )}
                            >
                              <span
                                className={cn(
                                  "flex items-center gap-2 text-base font-bold",
                                  draft.offFirst === option.value
                                    ? "text-[#0E59C6]"
                                    : "text-[#101828]"
                                )}
                              >
                                {draft.offFirst === option.value ? (
                                  <Check
                                    className="size-4 shrink-0"
                                    aria-hidden="true"
                                  />
                                ) : null}
                                {option.label}
                              </span>
                              <span className="mt-3 block text-sm leading-6 text-[#667085]">
                                {option.description}
                              </span>
                            </button>
                          ))}
                        </div>
                      </ConfigCard>

                      <ConfigCard
                        badge="2단계"
                        description="이번 근무표를 생성할 병동과 근무자 수를 확인합니다."
                        title="인원 반영"
                      >
                        <div className="flex items-center justify-between rounded-lg px-2 py-1.5">
                          <span className="text-sm font-semibold text-[#344054]">
                            병동명
                          </span>
                          <span className="max-w-[150px] truncate text-sm font-bold text-[#101828]">
                            {groupName}
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg px-2 py-1.5">
                          <span className="text-sm font-semibold text-[#344054]">
                            근무자 수
                          </span>
                          <span className="text-sm font-bold text-[#101828]">
                            {workerCount}명
                          </span>
                        </div>
                        <ConfigToggle
                          checked={draft.precepteeShiftCount}
                          label="프리셉티 포함"
                          note="프리셉티 근무를 생성 기준 인원 산정에 포함합니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("precepteeShiftCount", checked)
                          }
                        />
                        {SHOW_SAVED_CONFIGS && appliedPresetMessage && (
                          <p className="mt-2 rounded-md bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
                            {appliedPresetMessage}
                          </p>
                        )}
                        {startMode === "new" && (
                          <p className="mt-2 rounded-md bg-[#F8FBFF] px-2 py-1 text-xs font-bold text-[#0E59C6]">
                            새 설정으로 생성됩니다.
                          </p>
                        )}
                      </ConfigCard>

                      <ConfigCard
                        badge="3단계"
                        description="OFF 일수와 연속 OFF 관련 생성 기준입니다."
                        title="OFF 설정"
                      >
                        <NumberValue
                          label="월 OFF 수"
                          note="한 달에 배정할 기본 OFF 일수입니다."
                          suffix="일"
                          value={draft.offDays}
                          onChange={(value) => updateDraft("offDays", value)}
                        />
                        <NumberValue
                          label="연속 OFF 상한 (3-6일)"
                          min={MIN_CONSEQ_OFF_DAYS}
                          max={MAX_CONSEQ_OFF_DAYS}
                          note="해당 설정은 강제하지 않으며, 생성 상황에 따라 변경될 수 있습니다."
                          noteTone="caution"
                          suffix="일"
                          value={draft.maxConseqOff}
                          onChange={(value) =>
                            updateDraft("maxConseqOff", value)
                          }
                        />
                        <ConfigToggle
                          checked={draft.offSwapEnabled}
                          label="OFF 초과 시 연차 전환"
                          note="OFF가 기준보다 많아질 때 연차로 대체할 수 있게 합니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("offSwapEnabled", checked)
                          }
                        />
                        <ConfigToggle
                          checked={draft.healthLeaveEnabled}
                          label="보건휴가 자동 부여"
                          note="대상자에게 월 1개를 자동 배정합니다. 근무코드 설정에서 보건휴가 대상 코드를 먼저 지정해야 합니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("healthLeaveEnabled", checked)
                          }
                        />
                        {draft.healthLeaveEnabled ? (
                          <ConfigToggle
                            checked={draft.healthLeaveWeekend}
                            label="보건휴가 주말 배치 허용"
                            note="끄면 평일에만 배치합니다."
                            variant="compact"
                            onChange={(checked) =>
                              updateDraft("healthLeaveWeekend", checked)
                            }
                          />
                        ) : null}
                        <ConfigToggle
                          checked={draft.sleepOffEnabled}
                          label="수면OFF 자동 부여"
                          note="나이트 연번이 아래 주기에 도달하면 1개를 부여합니다. 근무코드 설정에서 수면OFF 대상 코드를 먼저 지정해야 합니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("sleepOffEnabled", checked)
                          }
                        />
                        {draft.sleepOffEnabled ? (
                          <NumberValue
                            label="수면OFF 트리거 주기"
                            note="나이트를 이 횟수만큼 서면 수면OFF 대상이 됩니다. 실무 기준은 15입니다."
                            suffix="회"
                            value={draft.sleepOffCycle}
                            onChange={(value) =>
                              updateDraft("sleepOffCycle", value)
                            }
                          />
                        ) : null}
                        <ConfigToggle
                          checked={draft.banNightBeforeFixedWantedOff}
                          label="신청 휴무 전날 나이트 금지"
                          note="신청해서 확정된 휴무 바로 앞날에는 나이트를 넣지 않습니다. 나이트 뒤 회복 휴무로 신청 휴무가 쓰이는 것을 막습니다. 자동으로 배정된 휴무는 해당하지 않으며, 휴가·공가 앞날은 별도로 이미 금지돼 있습니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("banNightBeforeFixedWantedOff", checked)
                          }
                        />
                      </ConfigCard>

                      <ConfigCard
                        badge="4단계"
                        description="연속 근무와 나이트 상한 기준입니다."
                        title="근무 설정"
                      >
                        <NumberValue
                          label="연속 근무 상한"
                          note="연속으로 배정 가능한 최대 근무 일수입니다."
                          suffix="일"
                          value={draft.maxConseqWork}
                          onChange={(value) =>
                            updateDraft("maxConseqWork", value)
                          }
                        />
                        <NumberValue
                          label="월 나이트 상한"
                          note="한 달에 배정 가능한 최대 N 근무 횟수입니다."
                          suffix="회"
                          value={draft.maxNightPerMonth}
                          onChange={(value) =>
                            updateDraft("maxNightPerMonth", value)
                          }
                        />
                        <ConfigToggle
                          checked={draft.threeSeqNight}
                          label="나이트 3연속 허용"
                          note="N 근무 3일 연속 배정을 허용합니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("threeSeqNight", checked)
                          }
                        />
                      </ConfigCard>

                      <ConfigCard
                        badge="5단계"
                        description="원티드와 나이트 배정 요청을 생성에 반영하는 기준입니다."
                        title="요청 반영"
                      >
                        <ConfigToggle
                          checked={draft.fixedWantedUse}
                          label="원티드 반영"
                          note="확정 요청을 생성 기준에 포함합니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("fixedWantedUse", checked)
                          }
                        />
                        <ConfigToggle
                          checked={draft.evenNights}
                          label="나이트 균형"
                          note="N 근무가 특정 인원에게 몰리지 않게 분산합니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("evenNights", checked)
                          }
                        />
                        <ConfigToggle
                          checked={draft.notOneNight}
                          label="단독 나이트 제한"
                          note="N 근무가 하루만 단독으로 끊기지 않게 요청 조건에 반영합니다."
                          variant="compact"
                          onChange={(checked) =>
                            updateDraft("notOneNight", checked)
                          }
                        />
                      </ConfigCard>
                    </div>
                  )}
                </>
              )}
            </div>

            {isStagedSetupActive ? (
              <footer className="shrink-0 border-t border-[#E4E7EC] bg-white px-7 py-5">
                <div className="flex items-center justify-between gap-5">
                  <p className="max-w-[520px] text-sm leading-relaxed text-[#667085]">
                    {summary}
                    <br />
                    마지막 단계에서 근무표 만들기를 누르면 생성 조건을
                    확인합니다.
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleStagedPrevious}
                      disabled={disabled || isBusy || stagedStep === 0}
                      className="h-[45px] min-w-[112px] rounded-full border-[#D0D5DD] text-sm font-bold text-[#344054] hover:border-[#B8CAE8] hover:bg-[#F8FAFC]"
                    >
                      이전
                    </Button>
                    {isLastStagedStep ? (
                      <Button
                        type="button"
                        onClick={handleStagedPrimaryAction}
                        disabled={disabled || isBusy || !canGenerate}
                        className="h-[45px] min-w-[156px] rounded-full border-0 bg-sig-gradient px-6 text-sm font-bold text-white shadow-[0_8px_18px_rgba(14,89,198,0.24)] hover:opacity-90"
                      >
                        근무표 만들기
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleStagedPrimaryAction}
                        disabled={disabled || isBusy || !canGenerate}
                        className="h-[45px] min-w-[112px] rounded-full bg-[#0E59C6] text-sm font-bold text-white hover:bg-[#0B4EAD] focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30"
                      >
                        다음
                        <ChevronRight
                          className="ml-1 size-4"
                          aria-hidden="true"
                        />
                      </Button>
                    )}
                  </div>
                </div>
              </footer>
            ) : (
              <footer className="shrink-0 border-t border-[#E4E7EC] bg-white px-7 py-5">
                <div className="flex items-center justify-between gap-5">
                  <p className="max-w-[460px] text-sm leading-relaxed text-[#667085]">
                    {isInitialConfigLoading
                      ? "최신 근무표 설정을 불러오는 중입니다."
                      : startMode === "choose"
                        ? "설정 시작 방식을 선택해주세요."
                        : summary}
                    <br />
                    {startMode === "choose"
                      ? "설정을 고른 뒤 단계별 설정 또는 전체 설정으로 이어집니다."
                      : startMode === "new"
                        ? "근무표 만들기 시점에 새 설정으로 적용합니다."
                        : hasSavedDraftChanges
                          ? "근무 조건을 바꿔서 저장 방식을 선택합니다."
                          : "불러온 설정을 기준으로 이번 생성에 적용합니다."}
                  </p>
                  {startMode !== "choose" && (
                    <div className="flex items-center gap-2">
                      <Button
                        hidden
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenSaveDialog("new")}
                        disabled={disabled || isBusy || !canGenerate}
                        className="h-[45px] w-[128px] rounded-full border-[#0E59C6] text-sm font-bold text-[#0E59C6]"
                      >
                        설정 저장
                      </Button>
                      <Button
                        type="button"
                        onClick={handleGenerate}
                        disabled={disabled || isBusy || !canGenerate}
                        className="h-[45px] w-[156px] rounded-full border-0 bg-sig-gradient text-sm font-bold text-white shadow-[0_8px_18px_rgba(14,89,198,0.24)] hover:opacity-90"
                      >
                        근무표 만들기
                      </Button>
                    </div>
                  )}
                </div>
              </footer>
            )}
            {showsBlockingOverlay && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
                <div className="rounded-lg bg-white px-10 py-5 shadow-2xl">
                  <LoadingSpiner
                    msg={
                      isDeletingPreset
                        ? "저장한 설정을 삭제 중입니다."
                        : pendingMsg
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isStagedConfirmDialogOpen}
        onOpenChange={(open) => {
          if (open) return;
          if (isBusy) return;
          keepStartPresetPickerOpenRef.current = false;
          setIsStagedConfirmDialogOpen(false);
        }}
      >
        <DialogContent
          overlayClassName="bg-black/35"
          className="w-[min(560px,calc(100vw-40px))] max-w-none gap-0 overflow-hidden rounded-xl border-0 bg-white p-0 font-pretendard shadow-xl"
        >
          <div className="border-b border-[#E4E7EC] px-6 pb-5 pt-6">
            <DialogTitle className="text-xl font-bold text-[#101828]">
              근무표를 생성하시겠습니까?
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-[#667085]">
              현재 설정과 조건을 확인한 뒤 근무표 만들기를 실행합니다.
            </DialogDescription>
          </div>
          <div className="max-h-[min(560px,calc(100dvh-260px))] overflow-y-auto px-6 py-5">
            <SetupModeSwitch
              contextLabel={setupContextLabel}
              description={
                setupContextDescription ||
                "현재 불러온 설정으로 생성합니다. 이 화면에서는 설정을 변경할 수 없습니다."
              }
              disabled={isBusy}
              notice={setupChangeNotice}
              readOnly
            />
            <div className="mt-5">
              <GenerationConfirmSummary
                draft={draft}
                groupName={groupName}
                workerCount={workerCount}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#E4E7EC] bg-white px-6 py-4">
            <Button
              type="button"
              variant="outline"
              disabled={isBusy}
              onClick={() => {
                keepStartPresetPickerOpenRef.current = false;
                setIsStagedConfirmDialogOpen(false);
              }}
              className="h-10 min-w-[96px] rounded-full border-[#D0D5DD] text-sm font-bold text-[#344054] hover:bg-[#F8FAFC]"
            >
              취소
            </Button>
            <Button
              type="button"
              disabled={disabled || isBusy || !canGenerate}
              onClick={handleStagedConfirmGenerate}
              className="h-10 min-w-[132px] rounded-full border-0 bg-sig-gradient px-5 text-sm font-bold text-white shadow-[0_8px_18px_rgba(14,89,198,0.24)] hover:opacity-90"
            >
              근무표 만들기
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <SavedConfigPickerDialog
        deletingPresetId={deletingPresetId}
        disabled={isBusy}
        emptyDescription="현재 설정을 저장하면 이곳에서 다시 불러올 수 있습니다."
        isOpen={SHOW_SAVED_CONFIGS && activeTab === "presets"}
        isPresetListLoading={isPresetListLoading}
        isPresetLoading={isPresetLoading}
        onClose={() => {
          if (isPresetLoading || isDeletingPreset) return;
          setSelectedEditPreset(null);
          setActiveTab("edit");
        }}
        onDeletePreset={handleDeletePreset}
        onEditPresetMeta={handleEditPresetMetaFromStart}
        onSelectPreset={(preset) => {
          setSelectedEditPreset(preset);
        }}
        presets={presets}
        currentPresetId={presetMeta.configId}
        favoriteStorageKey={favoriteStorageKey}
        selectedPresetId={selectedEditPreset?.config_id ?? presetMeta.configId}
        renderSelectedPresetModeActions={(preset, closeActions) => (
          <div className="grid gap-1">
            <button
              type="button"
              disabled={isBusy || isPresetLoading}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                closeActions();
                handleUseEditPreset("staged", preset);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (event.detail === 0) {
                  closeActions();
                  handleUseEditPreset("staged", preset);
                }
              }}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left text-[#101828] transition-colors hover:bg-[#F0F6FF] hover:text-[#0E59C6] focus-visible:bg-[#F0F6FF] focus-visible:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ClipboardList
                className="mt-0.5 size-4 shrink-0 text-[#0E59C6]"
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold">
                  단계별 설정으로 열기
                </span>
                <span className="mt-0.5 block text-xs font-medium text-[#667085]">
                  필수 항목부터 순서대로 설정
                </span>
              </span>
            </button>
            <button
              type="button"
              disabled={isBusy || isPresetLoading}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                closeActions();
                handleUseEditPreset("full", preset);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (event.detail === 0) {
                  closeActions();
                  handleUseEditPreset("full", preset);
                }
              }}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left text-[#344054] transition-colors hover:bg-[#F0F6FF] hover:text-[#0E59C6] focus-visible:bg-[#F0F6FF] focus-visible:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <SlidersHorizontal
                className="mt-0.5 size-4 shrink-0 text-[#667085]"
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold">
                  전체 설정으로 열기
                </span>
                <span className="mt-0.5 block text-xs font-medium text-[#667085]">
                  모든 조건을 한 화면에서 설정
                </span>
              </span>
            </button>
            <div className="my-1 h-px bg-[#EEF2F7]" />
            <button
              type="button"
              disabled={isBusy || isPresetLoading}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                closeActions();
                handleGenerateFromEditPreset(preset);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (event.detail === 0) {
                  closeActions();
                  handleGenerateFromEditPreset(preset);
                }
              }}
              className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left text-[#101828] transition-colors hover:bg-[#F0F6FF] hover:text-[#0E59C6] focus-visible:bg-[#F0F6FF] focus-visible:text-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Zap
                className="mt-0.5 size-4 shrink-0 text-[#F59E0B]"
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold">
                  근무표 즉시 생성
                </span>
                <span className="mt-0.5 block text-xs font-medium text-[#667085]">
                  저장된 설정으로 즉시 생성
                </span>
              </span>
            </button>
          </div>
        )}
      />
      <Dialog
        open={isReturnSaveChoiceOpen && pendingReturnAction === "start"}
        onOpenChange={(open) => {
          if (open || isSavingPreset) return;
          setIsReturnSaveChoiceOpen(false);
          setPendingReturnAction("none");
        }}
      >
        <DialogContent
          overlayClassName="bg-black/35"
          className="w-[min(500px,calc(100vw-40px))] max-w-none gap-0 overflow-hidden rounded-xl border-0 bg-white p-0 font-pretendard shadow-xl"
        >
          <div className="border-b border-[#E4E7EC] px-6 pb-5 pt-6">
            <span className="inline-flex rounded-full bg-[#FFF7E6] px-2.5 py-1 text-xs font-bold text-[#AD6800]">
              변경사항 있음
            </span>
            <DialogTitle className="mt-3 text-xl font-bold text-[#101828]">
              변경된 설정을 저장할까요?
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-[#667085]">
              저장하지 않고 돌아가면 방금 바꾼 설정값은 저장 설정에 남지
              않습니다.
            </DialogDescription>
          </div>
          <div className="space-y-3 px-6 py-5">
            <button
              type="button"
              disabled={isSavingPreset}
              onClick={() => void saveCurrentConfigForReturn()}
              className="w-full rounded-lg border border-[#0E59C6]/40 bg-[#F8FBFF] px-4 py-3.5 text-left transition-colors hover:border-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="block text-sm font-bold text-[#101828]">
                저장 후 돌아가기
              </span>
              <span className="mt-1 block text-xs leading-5 text-[#667085]">
                현재 조건을 저장한 뒤 설정 방식 선택 화면으로 돌아갑니다.
              </span>
            </button>
            <button
              type="button"
              disabled={isSavingPreset}
              onClick={returnToStartChooser}
              className="w-full rounded-lg border border-[#E4E7EC] bg-white px-4 py-3.5 text-left transition-colors hover:border-[#0E59C6]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="block text-sm font-bold text-[#101828]">
                저장하지 않고 돌아가기
              </span>
              <span className="mt-1 block text-xs leading-5 text-[#667085]">
                현재 화면의 편집값만 버리고 이전 선택 화면으로 돌아갑니다.
              </span>
            </button>
          </div>
          <div className="flex justify-end border-t border-[#E4E7EC] px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsReturnSaveChoiceOpen(false);
                setPendingReturnAction("none");
              }}
              disabled={isSavingPreset}
              className="h-10 w-[104px] rounded-lg border-[#D0D5DD] text-sm font-bold text-[#344054]"
            >
              계속 편집
            </Button>
          </div>
          {isSavingPreset && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
              <div className="rounded-lg bg-white px-8 py-5 shadow-xl">
                <LoadingSpiner msg="설정을 저장 중입니다." />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={isGenerateSaveChoiceOpen}
        onOpenChange={(open) => {
          if (open || isSavingPreset) return;
          setIsGenerateSaveChoiceOpen(false);
        }}
      >
        <DialogContent
          overlayClassName="bg-black/35"
          className="w-[min(500px,calc(100vw-40px))] max-w-none gap-0 overflow-hidden rounded-xl border-0 bg-white p-0 font-pretendard shadow-xl"
        >
          <div className="border-b border-[#E4E7EC] px-6 pb-5 pt-6">
            <span className="inline-flex rounded-full bg-[#EAF2FF] px-2.5 py-1 text-xs font-bold text-[#0E59C6]">
              저장 방식 선택
            </span>
            <DialogTitle className="mt-3 text-xl font-bold text-[#101828]">
              설정 변경사항이 있습니다
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-[#667085]">
              변경한 조건을 새 설정으로 저장하거나 기존 설정에 반영합니다.
            </DialogDescription>
          </div>
          <div className="space-y-3 px-6 py-5">
            <button
              type="button"
              disabled={isSavingPreset}
              onClick={() => void submitGenerate("create-new")}
              className="w-full rounded-lg border border-[#0E59C6]/40 bg-[#F8FBFF] px-4 py-3.5 text-left transition-colors hover:border-[#0E59C6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex items-center gap-2">
                <span className="text-sm font-bold text-[#101828]">
                  새 설정으로 저장
                </span>
                <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-[#0E59C6] ring-1 ring-[#B8CAE8]">
                  권장
                </span>
              </span>
              <span className="mt-1 block text-xs leading-5 text-[#667085]">
                기존 설정은 그대로 유지됩니다.
              </span>
            </button>
            <button
              type="button"
              disabled={isSavingPreset}
              onClick={() => void submitGenerate("update-existing")}
              className="w-full rounded-lg border border-[#E4E7EC] bg-white px-4 py-3.5 text-left transition-colors hover:border-[#0E59C6]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0E59C6]/30 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="block text-sm font-bold text-[#101828]">
                기존 설정 업데이트
              </span>
              <span className="mt-1 block text-xs leading-5 text-[#667085]">
                기존 저장 설정이 현재 조건으로 바뀝니다.
              </span>
            </button>
          </div>
          <div className="flex justify-end border-t border-[#E4E7EC] px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsGenerateSaveChoiceOpen(false)}
              disabled={isSavingPreset}
              className="h-10 w-[104px] rounded-lg border-[#D0D5DD] text-sm font-bold text-[#344054]"
            >
              설정 다시 보기
            </Button>
          </div>
          {isSavingPreset && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
              <div className="rounded-lg bg-white px-8 py-5 shadow-xl">
                <LoadingSpiner msg="저장 방식을 적용 중입니다." />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={isSaveDialogOpen}
        onOpenChange={(open) => {
          if (open || isSavingPreset) return;
          setIsSaveDialogOpen(false);
          restoreSavedPickerSurface(savedPickerSurfaceRef.current);
          savedPickerSurfaceRef.current = null;
        }}
      >
        <DialogContent
          overlayClassName="bg-black/35"
          className="w-[min(440px,calc(100vw-40px))] max-w-none gap-0 overflow-hidden rounded-xl border-0 bg-white p-0 font-pretendard shadow-xl"
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void handleSavePreset();
            }}
          >
            <div className="border-b border-[#E4E7EC] px-6 pb-5 pt-6">
              <DialogTitle className="text-xl font-bold text-[#101828]">
                {saveTarget !== "new" ? "설명 정보 수정" : "설정 저장"}
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-relaxed text-[#667085]">
                {saveTarget !== "new"
                  ? "목록에서 구분할 이름과 메모만 수정합니다. 근무 조건은 바뀌지 않습니다."
                  : "저장한 설정 목록에 표시될 이름과 메모를 입력합니다."}
              </DialogDescription>
            </div>

            <div className="space-y-4 px-6 py-5">
              <label className="block">
                <span className="text-sm font-bold text-[#344054]">
                  설정 이름
                </span>
                <input
                  autoFocus
                  value={savePresetForm.configName}
                  disabled={isSavingPreset}
                  onChange={(event) => {
                    const configName = event.currentTarget.value;
                    setSavePresetForm((prev) => ({
                      ...prev,
                      configName,
                    }));
                  }}
                  placeholder="예: 7월 9A 기본 설정"
                  className="mt-2 h-11 w-full rounded-lg border border-[#D0D5DD] bg-white px-3 text-sm font-semibold text-[#101828] outline-none focus:border-[#0E59C6] focus:ring-2 focus:ring-[#DCEBFF] disabled:cursor-not-allowed disabled:bg-[#F8FAFC] disabled:text-[#98A2B3]"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-[#344054]">메모</span>
                <textarea
                  value={savePresetForm.configMemo}
                  disabled={isSavingPreset}
                  onChange={(event) => {
                    const configMemo = event.currentTarget.value;
                    setSavePresetForm((prev) => ({
                      ...prev,
                      configMemo,
                    }));
                  }}
                  placeholder="이 설정을 구분할 설명을 입력하세요"
                  className="mt-2 min-h-[88px] w-full resize-none rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-sm leading-relaxed text-[#101828] outline-none focus:border-[#0E59C6] focus:ring-2 focus:ring-[#DCEBFF] disabled:cursor-not-allowed disabled:bg-[#F8FAFC] disabled:text-[#98A2B3]"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 border-t border-[#E4E7EC] px-6 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsSaveDialogOpen(false);
                  restoreSavedPickerSurface(savedPickerSurfaceRef.current);
                  savedPickerSurfaceRef.current = null;
                }}
                disabled={isSavingPreset}
                className="h-10 w-[88px] rounded-lg border-[#D0D5DD] text-sm font-bold text-[#344054]"
              >
                취소
              </Button>
              <Button
                type="submit"
                disabled={isSavingPreset}
                className="h-10 w-[104px] rounded-lg bg-[#0E59C6] text-sm font-bold text-white hover:bg-[#0B4EAD]"
              >
                {saveTarget !== "new" ? "수정 저장" : "저장하기"}
              </Button>
            </div>
          </form>

          {isSavingPreset && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
              <div className="rounded-lg bg-white px-8 py-5 shadow-xl">
                <LoadingSpiner
                  msg={
                    saveTarget !== "new"
                      ? "설명 정보를 수정 중입니다."
                      : "설정을 저장 중입니다."
                  }
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
